import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Scale, Users, Trophy, ChefHat, Sparkles, ChevronRight, ChevronLeft, Globe, Check, ArrowRight, ArrowLeft, MapPin, Pause, Share2, CalendarRange, Crown, Pencil, ChevronUp, ShoppingCart, Square, Home, MessageCircle, User, Navigation, Clock, Flame, Mic, Camera, Video, Languages, Medal, Search, RefreshCw, MoreHorizontal, Heart, Plus, UserCheck, LifeBuoy } from 'lucide-react';
import CookieConsent from '../components/CookieConsent';
import LandingExerciseSection from '../components/LandingExerciseSection';

const LOGO = 'https://customer-assets.emergentagent.com/job_57e2e740-274d-4480-9155-167f0e5dcf96/artifacts/q2sfm1zu_logo_white.png';

// Translations for landing page
const TRANSLATIONS = {
  it: {
    hero: {
      title: 'Perdi peso e',
      titleLine2: 'rimani in forma',
      subtitle: 'insieme alla community',
      description: 'Traccia i tuoi progressi, partecipa a sfide con amici e raggiungi i tuoi obiettivi con il supporto di una community motivata.',
      cta: 'Inizia Gratis',
      login: 'Accedi'
    },
    aiTransform: {
      title: 'Visualizza il tuo futuro',
      subtitle: 'con l\'AI',
      description: 'L\'intelligenza artificiale ti mostra come potresti apparire raggiungendo il tuo obiettivo.',
      stat: '+40%',
      statText: 'di probabilità di raggiungere l\'obiettivo visualizzandolo',
      before: 'Prima',
      after: 'Dopo',
      dragHint: 'Trascina per vedere la trasformazione'
    },
    social: {
      title: 'Condividi ogni momento',
      subtitle: 'del tuo percorso',
      description: 'La community ti supporta in ogni passo. Condividi i tuoi progressi e ispira gli altri.',
      types: [
        { title: 'Aggiornamenti Peso', desc: 'Festeggia ogni traguardo' },
        { title: 'Foto dei Pasti', desc: 'Mostra cosa mangi' },
        { title: 'Allenamenti', desc: 'Condividi le tue sessioni' },
        { title: 'Motivazione', desc: 'Ispira la community' },
        { title: 'Traguardi', desc: 'Celebra i successi' },
        { title: 'Sfide di Gruppo', desc: 'Competi con gli amici' }
      ]
    },
    challenges: {
      title: 'Sfide di gruppo',
      subtitle: 'per vincere insieme',
      description: 'Unisciti alle sfide con amici e raggiungi i tuoi obiettivi più velocemente.',
      challengeTitle: 'Sfida Estate 2026',
      goal: 'Obiettivo: -5kg in 30 giorni',
      participants: 'partecipanti',
      daysLeft: 'giorni rimasti',
      totalLost: 'kg persi dal gruppo',
      names: ['Marco', 'Giulia', 'Luca', 'Sofia', 'Andrea', 'Chiara', 'Matteo', 'Francesca']
    },
    mealPlan: {
      title: 'Piano Alimentare AI',
      subtitle: 'personalizzato per te',
      description: 'L\'intelligenza artificiale genera un piano alimentare settimanale completo, con ingredienti, quantità e istruzioni di preparazione calibrati sui tuoi obiettivi.',
      badge: 'Premium',
      features: [
        'Piano settimanale 7 giorni',
        'Quantità precise per ogni ingrediente',
        'Istruzioni di preparazione',
        'Personalizzato su allergie e preferenze'
      ],
      cta: 'Prova il Piano AI',
      header: 'Piano Alimentare AI',
      headerSub: 'Feature Premium',
      dailyAvg: 'Media giornaliera',
      balanced: 'Bilanciato',
      avgCal: '1497',
      calUnit: 'kcal/giorno',
      prepLabel: 'PREPARAZIONE',
      sample: {
        day: 'Lunedì',
        totalCal: '1550 kcal',
        meals: [
          { type: 'COLAZIONE', name: 'Porridge di avena', cal: '300', ingredients: '50g fiocchi d\'avena · 200ml latte di mandorle · 1 banana', preparation: 'Cuocere i fiocchi d\'avena con il latte di mandorle fino a ottenere una consistenza cremosa. Servire con la banana a fette sopra.', emoji: '🍌' },
          { type: 'SPUNTINO', name: 'Mela con burro di arachidi', cal: '200', ingredients: '1 mela · 1 cucchiaio burro di arachidi', preparation: 'Affettare la mela e spalmare il burro di arachidi sulle fette. Servire fresco.', emoji: '🍏' },
          { type: 'PRANZO', name: 'Insalata di quinoa', cal: '450', ingredients: '80g quinoa · 100g pollo · pomodorini · cetrioli', preparation: 'Cuocere la quinoa e il pollo. Unire le verdure tagliate e condire con olio e limone.', emoji: '🥗' }
        ]
      }
    },
    shoppingList: {
      title: 'Lista della Spesa AI',
      subtitle: 'dal tuo Piano Alimentare',
      description: 'Genera automaticamente la lista della spesa dal tuo piano alimentare. Scegli per quanti giorni e spunta gli ingredienti mentre fai acquisti.',
      badge: 'Premium',
      forHowManyDays: 'Per quanti giorni?',
      dayOptions: ['1 giorno', '2 giorni', '3 giorni', 'Settimana'],
      weekDays: 'Lunedì, Martedì, Mercoledì, Giovedì, Venerdì, Sabato, Domenica',
      progress: '0/10',
      categories: {
        produce: 'Frutta e Verdura',
        proteins: 'Carne e Pesce',
        dairy: 'Latticini e Uova',
        grains: 'Cereali e Legumi',
        other: 'Altro'
      },
      sampleItems: [
        '1 banana',
        '50g frutta mista',
        '100g insalata mista',
        '200g broccoli',
        '1 cucchiaio limone'
      ],
      proteinItems: [
        '150g petto di pollo',
        '150g salmone'
      ],
      dayLabel: 'Lunedì',
      cta: 'Genera la tua lista'
    },
    chefAi: {
      title: 'Chef AI',
      subtitle: 'il tuo nutrizionista personale',
      description: 'Genera ricette salutari personalizzate con l\'intelligenza artificiale. Puoi anche condividerle con la community!',
      recipe: {
        name: 'Pollo Grigliato Mediterraneo',
        time: '25 min',
        calories: '380 kcal',
        protein: '42g proteine',
        ingredients: ['Petto di pollo', 'Pomodorini', 'Olive', 'Feta', 'Rucola', 'Olio EVO'],
        shareText: 'Condividi con la community'
      },
      badge: 'Generato dall\'AI',
      tryIt: 'Prova Chef AI'
    },
    gpsTracker: {
      title: 'GPS Tracker',
      subtitle: 'per le tue camminate',
      description: 'Traccia i tuoi percorsi, monitora distanza, tempo e calorie bruciate. Condividi le tue camminate con la community!',
      walkTitle: 'Camminata',
      distance: 'Distanza',
      time: 'Tempo',
      calories: 'Calorie',
      pace: 'Passo',
      pause: 'Pausa',
      stop: 'Stop',
      startWalk: 'Inizia camminata',
      shareWalk: 'Condividi percorso',
      live: 'LIVE',
      buddiesLabel: 'In allenamento ora',
      buddies: [
        { name: 'Marco', km: '2.1', min: '18' },
        { name: 'Giulia', km: '3.4', min: '26' },
        { name: 'Luca', km: '1.8', min: '14' }
      ],
      cameraLabel: 'Foto',
      micLabel: 'Voce'
    },
    weightTracking: {
      title: 'Traccia il tuo peso',
      subtitle: 'con precisione scientifica',
      description: 'La curva biologica ti mostra il percorso ideale. I punti sotto la curva sono vittorie, quelli sopra sono opportunità di miglioramento.',
      start: 'Partenza',
      goal: 'Obiettivo',
      current: 'Peso attuale',
      lost: 'Persi finora',
      toGo: 'Mancano',
      monthsLabel: 'mesi'
    },
    features: {
      title: 'Tutto ciò di cui hai bisogno',
      items: [
        { icon: 'Scale', title: 'Tracciamento Peso', desc: 'Monitora i progressi con grafici dettagliati' },
        { icon: 'Users', title: 'Community Social', desc: 'Condividi e ispira gli altri' },
        { icon: 'Trophy', title: 'Sfide di Gruppo', desc: 'Competi con amici per restare motivato' },
        { icon: 'ChefHat', title: 'Chef AI', desc: 'Ricette personalizzate dall\'AI' },
        { icon: 'MapPin', title: 'GPS Tracker', desc: 'Traccia le tue camminate' },
        { icon: 'Sparkles', title: 'Trasformazione AI', desc: 'Visualizza il tuo obiettivo' }
      ]
    },
    stats: {
      users: 'Utenti attivi',
      weight: 'Kg persi',
      challenges: 'Sfide completate'
    },
    cta: {
      title: 'Inizia il tuo percorso oggi',
      subtitle: '100% Gratis',
      description: 'Tutte le funzionalità sono completamente gratuite. Nessun costo nascosto, nessun abbonamento obbligatorio.',
      button: 'Inizia Gratis Ora',
      noCard: 'Nessuna carta di credito richiesta • Gratis per sempre'
    },
    appStores: {
      comingSoon: 'Presto disponibile su',
      downloadOn: 'Scarica su',
      appStore: 'App Store',
      playStore: 'Google Play'
    },
    footer: {
      privacy: 'Privacy',
      terms: 'Termini',
      support: 'Assistenza',
      rights: 'Tutti i diritti riservati'
    },
    supportCard: {
      title: 'Hai bisogno di aiuto?',
      description: 'Scrivici per richieste di informazioni o assistenza.',
      cta: 'Contattaci'
    },
    leaderboard: {
      title: 'Classifica Nazionale',
      subtitle: 'in tempo reale',
      description: 'Scala la classifica del tuo paese ogni settimana. Compete su km, peso perso e attività completate. Riceverai notifiche push quando sali sul podio.',
      bullets: [
        'Top 1 nazionale ogni lunedì',
        'Classifica per paese, città e amici',
        'Notifiche push quando ti superano',
        'Storico settimanale e mensile'
      ],
      tag: 'Compete con il tuo paese'
    },
    languages: {
      title: 'Disponibile in 6 lingue',
      subtitle: 'community globale',
      description: 'Slimmy parla la tua lingua. L\'app, le notifiche e perfino la chat di assistenza si adattano automaticamente.',
      list: [
        { code: 'it', flag: '🇮🇹', name: 'Italiano' },
        { code: 'en', flag: '🇬🇧', name: 'English' },
        { code: 'es', flag: '🇪🇸', name: 'Español' },
        { code: 'pt', flag: '🇧🇷', name: 'Português' },
        { code: 'de', flag: '🇩🇪', name: 'Deutsch' },
        { code: 'fr', flag: '🇫🇷', name: 'Français' }
      ],
      ctaTag: 'Cambia lingua quando vuoi'
    },
    pricing: {
      title: 'Inizia gratis,',
      subtitle: 'sblocca il Premium quando vuoi',
      description: 'Tutte le funzionalità essenziali sono gratuite. Il Premium aggiunge AI illimitata, piani alimentari, classifiche complete e altro.',
      free: {
        name: 'Free',
        price: '0€',
        period: 'per sempre',
        features: [
          'Tracker peso illimitato',
          'Sfide community',
          'Camminata GPS',
          'Feed social con foto e video',
          'Walkie-talkie con compagni di sfida',
          'Foto e video LIVE durante l\'allenamento'
        ],
        cta: 'Inizia gratis'
      },
      premium: {
        name: 'Premium',
        price: '4,99€',
        period: '/ mese',
        badge: 'Consigliato',
        features: [
          'Tutto il piano Free',
          'AI Body Transformation settimanale',
          'Piani alimentari AI personalizzati',
          'Chef AI per ricette su misura',
          'Classifiche dettagliate e storico',
          'Statistiche avanzate'
        ],
        cta: 'Prova Premium'
      },
      lifetimeBadge: 'Primi 1.000 utenti: Premium gratis a vita 🎁'
    },
      walkieTalkie: {
        title: 'Walkie-talkie',
        subtitle: 'durante l\'allenamento',
        description: 'Parla in tempo reale con un compagno di allenamento. Premi e tieni per parlare, rilascia per ascoltare. Perfetto per camminate, corse e bici.',
        bullets: [
          'Audio live a bassa latenza',
          'Push-to-talk con un solo dito',
          'Funziona anche con schermo bloccato',
          'Sincronizzato col tracker GPS'
        ],
        hint: 'Tieni premuto per parlare',
        live: 'LIVE',
        premium: 'Solo con compagni di sfida attivi'
      },
      shareWorkout: {
        title: 'Foto e video LIVE',
        subtitle: 'durante l\'allenamento',
        description: 'Mentre cammini o corri, puoi scattare foto e brevi video direttamente dal tracker e condividerli istantaneamente con la community. A fine allenamento, il post viene completato in automatico con mappa GPS e statistiche.',
        bullets: [
          'Foto in tempo reale durante l\'esercizio',
          'Video brevi (max 60 sec) mentre ti muovi',
          'Mappa GPS del percorso allegata al post',
          'Statistiche calcolate automaticamente'
        ],
        stats: { distance: 'Distanza', time: 'Tempo', calories: 'Calorie', pace: 'Passo' },
        caption: '5km in collina 🏃‍♂️ Nuovo record personale!',
        tag: 'Condivisione live',
        feedMock: {
          title: 'Feed',
          tabs: { all: 'Tutti', following: 'Tribù', challenges: 'Sfide' },
          visibility: 'Pubblico',
          bottomNav: { home: 'Home', feed: 'Feed', challenges: 'Sfide', profile: 'Profilo' },
          categories: { motivation: 'Motivazione', food: 'Cibo', workout: 'Allenamento', walk: 'Camminata' },
          posts: [
            { name: 'Giulia', isPremium: false, category: 'food',    timeAgo: '2 ore fa',     caption: 'Quando dici "una porzione" ma sai che ne mangerai 4 😂', photoIdx: 1 },
            { name: 'Marco',  isPremium: true,  category: 'workout',    timeAgo: '39 minuti fa', caption: 'Io e Max trekking sulla neve 🏔️',                photoIdx: 0, videoIdx: 0 },
            { name: 'Sofia',  isPremium: false, category: 'walk',    timeAgo: 'ieri',         caption: 'Passeggiata di 3 km in montagna 🏔️',                    photoIdx: 3 },
            { name: 'Frenk',  isPremium: true,  category: 'workout', timeAgo: '1 giorno fa',  caption: 'Anche oggi un po di movimento 🚶‍♂️',                             photoIdx: 4, videoIdx: 1 },
          ]
        }
      },
    nav: {
      home: 'Home',
      feed: 'Feed',
      challenges: 'Sfide',
      profile: 'Profilo'
    }
  },
  en: {
    hero: {
      title: 'Lose weight and',
      titleLine2: 'stay fit',
      subtitle: 'with the community',
      description: 'Track your progress, join challenges with friends and reach your goals with the support of a motivated community.',
      cta: 'Start Free',
      login: 'Login'
    },
    aiTransform: {
      title: 'Visualize your future',
      subtitle: 'with AI',
      description: 'Artificial intelligence shows you how you could look when you reach your goal.',
      stat: '+40%',
      statText: 'more likely to reach your goal by visualizing it',
      before: 'Before',
      after: 'After',
      dragHint: 'Drag to see the transformation'
    },
    social: {
      title: 'Share every moment',
      subtitle: 'of your journey',
      description: 'The community supports you every step of the way. Share your progress and inspire others.',
      types: [
        { title: 'Weight Updates', desc: 'Celebrate every milestone' },
        { title: 'Meal Photos', desc: 'Show what you eat' },
        { title: 'Workouts', desc: 'Share your sessions' },
        { title: 'Motivation', desc: 'Inspire the community' },
        { title: 'Milestones', desc: 'Celebrate success' },
        { title: 'Group Challenges', desc: 'Compete with friends' }
      ]
    },
    challenges: {
      title: 'Group challenges',
      subtitle: 'to win together',
      description: 'Join challenges with friends and reach your goals faster.',
      challengeTitle: 'Summer Challenge 2026',
      goal: 'Goal: -5kg in 30 days',
      participants: 'participants',
      daysLeft: 'days left',
      totalLost: 'kg lost by group',
      names: ['James', 'Emma', 'Michael', 'Olivia', 'William', 'Sophia', 'Daniel', 'Isabella']
    },
    mealPlan: {
      title: 'AI Meal Plan',
      subtitle: 'personalized for you',
      description: 'Artificial intelligence generates a complete weekly meal plan with ingredients, quantities, and preparation instructions tailored to your goals.',
      badge: 'Premium',
      features: [
        '7-day weekly plan',
        'Precise quantities for every ingredient',
        'Preparation instructions',
        'Customized for allergies and preferences'
      ],
      cta: 'Try AI Plan',
      header: 'AI Meal Plan',
      headerSub: 'Premium Feature',
      dailyAvg: 'Daily average',
      balanced: 'Balanced',
      avgCal: '1497',
      calUnit: 'kcal/day',
      prepLabel: 'PREPARATION',
      sample: {
        day: 'Monday',
        totalCal: '1550 kcal',
        meals: [
          { type: 'BREAKFAST', name: 'Oat porridge', cal: '300', ingredients: '50g oat flakes · 200ml almond milk · 1 banana', preparation: 'Cook oat flakes in almond milk until creamy. Serve with sliced banana on top.', emoji: '🍌' },
          { type: 'SNACK', name: 'Apple with peanut butter', cal: '200', ingredients: '1 apple · 1 tbsp peanut butter', preparation: 'Slice the apple and spread peanut butter on the slices. Serve fresh.', emoji: '🍏' },
          { type: 'LUNCH', name: 'Quinoa salad', cal: '450', ingredients: '80g quinoa · 100g chicken · cherry tomatoes · cucumbers', preparation: 'Cook quinoa and chicken. Mix with diced vegetables and dress with oil and lemon.', emoji: '🥗' }
        ]
      }
    },
    shoppingList: {
      title: 'AI Shopping List',
      subtitle: 'from your Meal Plan',
      description: 'Automatically generate your shopping list from your meal plan. Choose how many days and check off ingredients as you shop.',
      badge: 'Premium',
      forHowManyDays: 'For how many days?',
      dayOptions: ['1 day', '2 days', '3 days', 'Week'],
      weekDays: 'Monday, Tuesday, Wednesday, Thursday, Friday, Saturday, Sunday',
      progress: '0/10',
      categories: {
        produce: 'Fruits & Vegetables',
        proteins: 'Meat & Fish',
        dairy: 'Dairy & Eggs',
        grains: 'Grains & Legumes',
        other: 'Other'
      },
      sampleItems: [
        '1 banana',
        '50g mixed fruit',
        '100g mixed salad',
        '200g broccoli',
        '1 tbsp lemon'
      ],
      proteinItems: [
        '150g chicken breast',
        '150g salmon'
      ],
      dayLabel: 'Monday',
      cta: 'Generate your list'
    },
    chefAi: {
      title: 'AI Chef',
      subtitle: 'your personal nutritionist',
      description: 'Generate personalized healthy recipes with artificial intelligence. You can also share them with the community!',
      recipe: {
        name: 'Mediterranean Grilled Chicken',
        time: '25 min',
        calories: '380 kcal',
        protein: '42g protein',
        ingredients: ['Chicken breast', 'Cherry tomatoes', 'Olives', 'Feta', 'Arugula', 'Olive oil'],
        shareText: 'Share with community'
      },
      badge: 'AI Generated',
      tryIt: 'Try AI Chef'
    },
    gpsTracker: {
      title: 'GPS Tracker',
      subtitle: 'for your walks',
      description: 'Track your routes, monitor distance, time and calories burned. Share your walks with the community!',
      walkTitle: 'Walk',
      distance: 'Distance',
      time: 'Time',
      calories: 'Calories',
      pace: 'Pace',
      pause: 'Pause',
      stop: 'Stop',
      startWalk: 'Start walk',
      shareWalk: 'Share route',
      live: 'LIVE',
      buddiesLabel: 'Working out now',
      buddies: [
        { name: 'Mark', km: '2.1', min: '18' },
        { name: 'Emma', km: '3.4', min: '26' },
        { name: 'Jake', km: '1.8', min: '14' }
      ],
      cameraLabel: 'Photo',
      micLabel: 'Voice'
    },
    weightTracking: {
      title: 'Track your weight',
      subtitle: 'with scientific precision',
      description: 'The biological curve shows you the ideal path. Points below the curve are victories, those above are opportunities for improvement.',
      start: 'Start',
      goal: 'Goal',
      current: 'Current weight',
      lost: 'Lost so far',
      toGo: 'To go',
      monthsLabel: 'months'
    },
    features: {
      title: 'Everything you need',
      items: [
        { icon: 'Scale', title: 'Weight Tracking', desc: 'Monitor progress with detailed charts' },
        { icon: 'Users', title: 'Social Community', desc: 'Share and inspire others' },
        { icon: 'Trophy', title: 'Group Challenges', desc: 'Compete with friends to stay motivated' },
        { icon: 'ChefHat', title: 'AI Chef', desc: 'AI-powered personalized recipes' },
        { icon: 'MapPin', title: 'GPS Tracker', desc: 'Track your walks' },
        { icon: 'Sparkles', title: 'AI Transformation', desc: 'Visualize your goal' }
      ]
    },
    stats: {
      users: 'Active users',
      weight: 'Kg lost',
      challenges: 'Challenges completed'
    },
    cta: {
      title: 'Start your journey today',
      subtitle: '100% Free',
      description: 'All features are completely free. No hidden costs, no mandatory subscription.',
      button: 'Start Free Now',
      noCard: 'No credit card required • Free forever'
    },
    appStores: {
      comingSoon: 'Coming soon on',
      downloadOn: 'Download on the',
      appStore: 'App Store',
      playStore: 'Google Play'
    },
    footer: {
      privacy: 'Privacy',
      terms: 'Terms',
      support: 'Support',
      rights: 'All rights reserved'
    },
    supportCard: {
      title: 'Need help?',
      description: 'Write to us for information or support requests.',
      cta: 'Contact us'
    },
    leaderboard: {
      title: 'National Leaderboard',
      subtitle: 'in real time',
      description: 'Climb the leaderboard of your country every week. Compete on km, weight lost and completed activities. You\'ll get push notifications when you\'re on the podium.',
      bullets: [
        'National Top 1 every Monday',
        'Ranking by country, city and friends',
        'Push notifications when overtaken',
        'Weekly and monthly history'
      ],
      tag: 'Compete with your country'
    },
    languages: {
      title: 'Available in 6 languages',
      subtitle: 'global community',
      description: 'Slimmy speaks your language. The app, notifications and even support chat adapt automatically.',
      list: [
        { code: 'it', flag: '🇮🇹', name: 'Italiano' },
        { code: 'en', flag: '🇬🇧', name: 'English' },
        { code: 'es', flag: '🇪🇸', name: 'Español' },
        { code: 'pt', flag: '🇧🇷', name: 'Português' },
        { code: 'de', flag: '🇩🇪', name: 'Deutsch' },
        { code: 'fr', flag: '🇫🇷', name: 'Français' }
      ],
      ctaTag: 'Change language whenever you want'
    },
    pricing: {
      title: 'Start free,',
      subtitle: 'unlock Premium whenever you want',
      description: 'All core features are free. Premium adds unlimited AI, meal plans, full leaderboards and more.',
      free: {
        name: 'Free',
        price: '$0',
        period: 'forever',
        features: [
          'Unlimited weight tracker',
          'Community challenges',
          'GPS walk',
          'Social feed with photos & videos',
          'Walkie-talkie with challenge buddies',
          'LIVE photos & videos during workout'
        ],
        cta: 'Start for free'
      },
      premium: {
        name: 'Premium',
        price: '$4.99',
        period: '/ month',
        badge: 'Recommended',
        features: [
          'Everything in Free',
          'Weekly AI Body Transformation',
          'Personalized AI meal plans',
          'AI Chef for tailored recipes',
          'Detailed leaderboards & history',
          'Advanced stats'
        ],
        cta: 'Try Premium'
      },
      lifetimeBadge: 'First 1,000 users: free Lifetime Premium 🎁'
    },
    walkieTalkie: {
      title: 'Walkie-talkie',
      subtitle: 'during your workout',
      description: 'Talk in real time with a workout buddy. Press and hold to talk, release to listen. Perfect for walks, runs and bike rides.',
      bullets: [
        'Low-latency live audio',
        'One-finger push-to-talk',
        'Works with screen locked',
        'Synced with the GPS tracker'
      ],
      hint: 'Hold to talk',
      live: 'LIVE',
      premium: 'Live workout buddies only'
    },
    shareWorkout: {
      title: 'Live photos & videos',
      subtitle: 'during your workout',
      description: 'While walking or running, you can snap photos and short videos directly from the tracker and share them instantly with the community. At the end of the workout, the post is automatically completed with the GPS map and stats.',
      bullets: [
        'Real-time photos during exercise',
        'Short videos (max 60 sec) on the move',
        'GPS route map attached to the post',
        'Stats calculated automatically'
      ],
      stats: { distance: 'Distance', time: 'Time', calories: 'Calories', pace: 'Pace' },
      caption: '5km hill run 🏃‍♂️ New personal record!',
      tag: 'Live sharing',
      feedMock: {
        title: 'Feed',
        tabs: { all: 'All', following: 'Tribe', challenges: 'Challenges' },
        visibility: 'Public',
        bottomNav: { home: 'Home', feed: 'Feed', challenges: 'Challenges', profile: 'Profile' },
        categories: { motivation: 'Motivation', food: 'Food', workout: 'Workout', walk: 'Walk' },
        posts: [
          { name: 'Emma',    isPremium: false, category: 'food',    timeAgo: '2 hr ago',    caption: 'When you say "just one plate" but already know you\'ll eat 4 😂', photoIdx: 1 },
          { name: 'James',   isPremium: true,  category: 'workout',    timeAgo: '39 min ago',  caption: 'Trekking in the snow with Max 🏔️',                            photoIdx: 0, videoIdx: 0 },
          { name: 'Sophie',  isPremium: false, category: 'walk',    timeAgo: 'yesterday',   caption: '3 km mountain walk 🏔️',                                         photoIdx: 3 },
          { name: 'Liam',    isPremium: true,  category: 'workout', timeAgo: '1 day ago',   caption: 'A bit of movement today too 🚶‍♂️',                                photoIdx: 4, videoIdx: 1 },
        ]
      }
    },
    nav: {
      home: 'Home',
      feed: 'Feed',
      challenges: 'Challenges',
      profile: 'Profile'
    }
  },
  es: {
    hero: {
      title: 'Pierde peso y',
      titleLine2: 'mantente en forma',
      subtitle: 'junto a la comunidad',
      description: 'Rastrea tu progreso, participa en retos con amigos y alcanza tus metas con el apoyo de una comunidad motivada.',
      cta: 'Empieza Gratis',
      login: 'Acceder'
    },
    aiTransform: {
      title: 'Visualiza tu futuro',
      subtitle: 'con IA',
      description: 'La inteligencia artificial te muestra cómo podrías verte al alcanzar tu objetivo.',
      stat: '+40%',
      statText: 'más probabilidad de alcanzar tu objetivo visualizándolo',
      before: 'Antes',
      after: 'Después',
      dragHint: 'Arrastra para ver la transformación'
    },
    social: {
      title: 'Comparte cada momento',
      subtitle: 'de tu viaje',
      description: 'La comunidad te apoya en cada paso. Comparte tu progreso e inspira a otros.',
      types: [
        { title: 'Peso', desc: 'Celebra cada logro' },
        { title: 'Comidas', desc: 'Muestra lo que comes' },
        { title: 'Entrenamientos', desc: 'Comparte tus sesiones' },
        { title: 'Motivación', desc: 'Inspira la comunidad' },
        { title: 'Logros', desc: 'Celebra el éxito' },
        { title: 'Retos', desc: 'Compite con amigos' }
      ]
    },
    challenges: {
      title: 'Retos grupales',
      subtitle: 'para ganar juntos',
      description: 'Únete a retos con amigos y alcanza tus metas más rápido.',
      challengeTitle: 'Reto Verano 2026',
      goal: 'Objetivo: -5kg en 30 días',
      participants: 'participantes',
      daysLeft: 'días restantes',
      totalLost: 'kg perdidos por el grupo',
      names: ['Carlos', 'María', 'Pablo', 'Lucía', 'Diego', 'Ana', 'Javier', 'Carmen']
    },
    mealPlan: {
      title: 'Plan Alimentario IA',
      subtitle: 'personalizado para ti',
      description: 'La inteligencia artificial genera un plan alimentario semanal completo, con ingredientes, cantidades e instrucciones de preparación adaptados a tus objetivos.',
      badge: 'Premium',
      features: [
        'Plan semanal de 7 días',
        'Cantidades precisas por ingrediente',
        'Instrucciones de preparación',
        'Personalizado según alergias y preferencias'
      ],
      cta: 'Probar Plan IA',
      header: 'Plan Alimentario IA',
      headerSub: 'Función Premium',
      dailyAvg: 'Media diaria',
      balanced: 'Equilibrado',
      avgCal: '1497',
      calUnit: 'kcal/día',
      prepLabel: 'PREPARACIÓN',
      sample: {
        day: 'Lunes',
        totalCal: '1550 kcal',
        meals: [
          { type: 'DESAYUNO', name: 'Porridge de avena', cal: '300', ingredients: '50g copos de avena · 200ml leche de almendras · 1 plátano', preparation: 'Cocinar los copos de avena con la leche de almendras hasta obtener una consistencia cremosa. Servir con el plátano en rodajas.', emoji: '🍌' },
          { type: 'SNACK', name: 'Manzana con mantequilla de cacahuete', cal: '200', ingredients: '1 manzana · 1 cucharada mantequilla de cacahuete', preparation: 'Cortar la manzana en rodajas y untar la mantequilla de cacahuete. Servir fresco.', emoji: '🍏' },
          { type: 'ALMUERZO', name: 'Ensalada de quinoa', cal: '450', ingredients: '80g quinoa · 100g pollo · tomates cherry · pepinos', preparation: 'Cocinar la quinoa y el pollo. Mezclar con las verduras cortadas y aliñar con aceite y limón.', emoji: '🥗' }
        ]
      }
    },
    shoppingList: {
      title: 'Lista de Compras IA',
      subtitle: 'de tu Plan Alimentario',
      description: 'Genera automáticamente la lista de compras desde tu plan alimentario. Elige cuántos días y marca los ingredientes mientras compras.',
      badge: 'Premium',
      forHowManyDays: '¿Para cuántos días?',
      dayOptions: ['1 día', '2 días', '3 días', 'Semana'],
      weekDays: 'Lunes, Martes, Miércoles, Jueves, Viernes, Sábado, Domingo',
      progress: '0/10',
      categories: {
        produce: 'Frutas y Verduras',
        proteins: 'Carne y Pescado',
        dairy: 'Lácteos y Huevos',
        grains: 'Cereales y Legumbres',
        other: 'Otros'
      },
      sampleItems: [
        '1 plátano',
        '50g fruta mixta',
        '100g ensalada mixta',
        '200g brócoli',
        '1 cucharada limón'
      ],
      proteinItems: [
        '150g pechuga de pollo',
        '150g salmón'
      ],
      dayLabel: 'Lunes',
      cta: 'Genera tu lista'
    },
    chefAi: {
      title: 'Chef IA',
      subtitle: 'tu nutricionista personal',
      description: 'Genera recetas saludables personalizadas con inteligencia artificial. ¡También puedes compartirlas con la comunidad!',
      recipe: {
        name: 'Pollo Mediterráneo a la Parrilla',
        time: '25 min',
        calories: '380 kcal',
        protein: '42g proteína',
        ingredients: ['Pechuga de pollo', 'Tomates cherry', 'Aceitunas', 'Feta', 'Rúcula', 'Aceite de oliva'],
        shareText: 'Compartir con la comunidad'
      },
      badge: 'Generado por IA',
      tryIt: 'Probar Chef IA'
    },
    gpsTracker: {
      title: 'GPS Tracker',
      subtitle: 'para tus caminatas',
      description: 'Rastrea tus rutas, monitorea distancia, tiempo y calorías quemadas. ¡Comparte tus caminatas con la comunidad!',
      walkTitle: 'Caminata',
      distance: 'Distancia',
      time: 'Tiempo',
      calories: 'Calorías',
      pace: 'Ritmo',
      pause: 'Pausa',
      stop: 'Detener',
      startWalk: 'Iniciar caminata',
      shareWalk: 'Compartir ruta',
      live: 'EN VIVO',
      buddiesLabel: 'Entrenando ahora',
      buddies: [
        { name: 'Carlos', km: '2.1', min: '18' },
        { name: 'Lucía', km: '3.4', min: '26' },
        { name: 'Diego', km: '1.8', min: '14' }
      ],
      cameraLabel: 'Foto',
      micLabel: 'Voz'
    },
    weightTracking: {
      title: 'Rastrea tu peso',
      subtitle: 'con precisión científica',
      description: 'La curva biológica te muestra el camino ideal. Los puntos bajo la curva son victorias, los de arriba son oportunidades de mejora.',
      start: 'Inicio',
      goal: 'Objetivo',
      current: 'Peso actual',
      lost: 'Perdidos',
      toGo: 'Faltan',
      monthsLabel: 'meses'
    },
    features: {
      title: 'Todo lo que necesitas',
      items: [
        { icon: 'Scale', title: 'Registro de Peso', desc: 'Monitorea el progreso con gráficos' },
        { icon: 'Users', title: 'Comunidad Social', desc: 'Comparte e inspira a otros' },
        { icon: 'Trophy', title: 'Retos Grupales', desc: 'Compite con amigos' },
        { icon: 'ChefHat', title: 'Chef IA', desc: 'Recetas personalizadas con IA' },
        { icon: 'MapPin', title: 'GPS Tracker', desc: 'Rastrea tus caminatas' },
        { icon: 'Sparkles', title: 'Transformación IA', desc: 'Visualiza tu objetivo' }
      ]
    },
    stats: {
      users: 'Usuarios activos',
      weight: 'Kg perdidos',
      challenges: 'Retos completados'
    },
    cta: {
      title: 'Empieza tu viaje hoy',
      subtitle: '100% Gratis',
      description: 'Todas las funciones son completamente gratuitas. Sin costos ocultos, sin suscripción obligatoria.',
      button: 'Empieza Gratis Ahora',
      noCard: 'Sin tarjeta de crédito • Gratis para siempre'
    },
    appStores: {
      comingSoon: 'Próximamente en',
      downloadOn: 'Descargar en',
      appStore: 'App Store',
      playStore: 'Google Play'
    },
    footer: {
      privacy: 'Privacidad',
      terms: 'Términos',
      support: 'Asistencia',
      rights: 'Todos los derechos reservados'
    },
    supportCard: {
      title: '¿Necesitas ayuda?',
      description: 'Escríbenos para solicitudes de información o asistencia.',
      cta: 'Contáctanos'
    },
    leaderboard: {
      title: 'Clasificación Nacional',
      subtitle: 'en tiempo real',
      description: 'Sube en la clasificación de tu país cada semana. Compite en km, peso perdido y actividades completadas. Recibirás notificaciones push cuando estés en el podio.',
      bullets: [
        'Top 1 nacional cada lunes',
        'Clasificación por país, ciudad y amigos',
        'Notificaciones push cuando te superan',
        'Historial semanal y mensual'
      ],
      tag: 'Compite con tu país'
    },
    languages: {
      title: 'Disponible en 6 idiomas',
      subtitle: 'comunidad global',
      description: 'Slimmy habla tu idioma. La app, las notificaciones e incluso el chat de soporte se adaptan automáticamente.',
      list: [
        { code: 'it', flag: '🇮🇹', name: 'Italiano' },
        { code: 'en', flag: '🇬🇧', name: 'English' },
        { code: 'es', flag: '🇪🇸', name: 'Español' },
        { code: 'pt', flag: '🇧🇷', name: 'Português' },
        { code: 'de', flag: '🇩🇪', name: 'Deutsch' },
        { code: 'fr', flag: '🇫🇷', name: 'Français' }
      ],
      ctaTag: 'Cambia de idioma cuando quieras'
    },
    pricing: {
      title: 'Empieza gratis,',
      subtitle: 'desbloquea Premium cuando quieras',
      description: 'Todas las funciones principales son gratuitas. Premium añade IA ilimitada, planes de comidas, clasificaciones completas y más.',
      free: {
        name: 'Free',
        price: '0€',
        period: 'para siempre',
        features: [
          'Tracker de peso ilimitado',
          'Retos de la comunidad',
          'Caminata GPS',
          'Feed social con fotos y vídeos',
          'Walkie-talkie con compañeros de reto',
          'Fotos y vídeos EN VIVO durante el entreno'
        ],
        cta: 'Empezar gratis'
      },
      premium: {
        name: 'Premium',
        price: '4,99€',
        period: '/ mes',
        badge: 'Recomendado',
        features: [
          'Todo lo del plan Free',
          'AI Body Transformation semanal',
          'Planes de comidas con IA personalizados',
          'Chef IA para recetas a medida',
          'Clasificaciones detalladas e historial',
          'Estadísticas avanzadas'
        ],
        cta: 'Probar Premium'
      },
      lifetimeBadge: 'Primeros 1.000 usuarios: Premium gratis de por vida 🎁'
    },
    walkieTalkie: {
      title: 'Walkie-talkie',
      subtitle: 'durante el entrenamiento',
      description: 'Habla en tiempo real con un compañero de entrenamiento. Pulsa y mantén para hablar, suelta para escuchar. Perfecto para caminatas, carreras y bici.',
      bullets: [
        'Audio en vivo de baja latencia',
        'Push-to-talk con un solo dedo',
        'Funciona incluso con pantalla bloqueada',
        'Sincronizado con el tracker GPS'
      ],
      hint: 'Mantén pulsado para hablar',
      live: 'EN VIVO',
      premium: 'Solo con compañeros de reto activos'
    },
    shareWorkout: {
      title: 'Fotos y vídeos EN VIVO',
      subtitle: 'durante el entrenamiento',
      description: 'Mientras caminas o corres, puedes hacer fotos y vídeos cortos directamente desde el tracker y compartirlos al instante con la comunidad. Al finalizar, el post se completa automáticamente con la ruta GPS y las estadísticas.',
      bullets: [
        'Fotos en tiempo real durante el ejercicio',
        'Vídeos cortos (máx. 60 seg) en movimiento',
        'Mapa GPS del recorrido adjunto al post',
        'Estadísticas calculadas automáticamente'
      ],
      stats: { distance: 'Distancia', time: 'Tiempo', calories: 'Calorías', pace: 'Ritmo' },
      caption: '5km en colinas 🏃‍♂️ ¡Nuevo récord personal!',
      tag: 'Compartir en vivo',
      feedMock: {
        title: 'Feed',
        tabs: { all: 'Todos', following: 'Tribu', challenges: 'Retos' },
        visibility: 'Público',
        bottomNav: { home: 'Inicio', feed: 'Feed', challenges: 'Retos', profile: 'Perfil' },
        categories: { motivation: 'Motivación', food: 'Comida', workout: 'Entreno', walk: 'Caminata' },
        posts: [
          { name: 'Lucía',  isPremium: false, category: 'food',    timeAgo: 'hace 2 h',     caption: 'Cuando dices "solo un plato" pero sabes que te comerás 4 😂',        photoIdx: 1 },
          { name: 'Marco',  isPremium: true,  category: 'workout',    timeAgo: 'hace 39 min',  caption: 'Trekking en la nieve con Max 🏔️',                          photoIdx: 0, videoIdx: 0 },
          { name: 'Sofía',  isPremium: false, category: 'walk',    timeAgo: 'ayer',         caption: 'Caminata de 3 km en la montaña 🏔️',                                  photoIdx: 3 },
          { name: 'Javier', isPremium: true,  category: 'workout', timeAgo: 'hace 1 día',   caption: 'Un poco de movimiento hoy también 🚶‍♂️',                                              photoIdx: 4, videoIdx: 1 },
        ]
      }
    },
    nav: {
      home: 'Inicio',
      feed: 'Muro',
      challenges: 'Retos',
      profile: 'Perfil'
    }
  },
  pt: {
    hero: {
      title: 'Perca peso e',
      titleLine2: 'fique em forma',
      subtitle: 'com a comunidade',
      description: 'Acompanhe seu progresso, participe de desafios com amigos e alcance suas metas com o apoio de uma comunidade motivada.',
      cta: 'Comece Grátis',
      login: 'Entrar'
    },
    aiTransform: {
      title: 'Visualize seu futuro',
      subtitle: 'com IA',
      description: 'A inteligência artificial mostra como você poderia parecer ao atingir seu objetivo.',
      stat: '+40%',
      statText: 'mais chance de atingir seu objetivo visualizando-o',
      before: 'Antes',
      after: 'Depois',
      dragHint: 'Arraste para ver a transformação'
    },
    social: {
      title: 'Compartilhe cada momento',
      subtitle: 'da sua jornada',
      description: 'A comunidade te apoia em cada passo. Compartilhe seu progresso e inspire outros.',
      types: [
        { title: 'Peso', desc: 'Celebre cada conquista' },
        { title: 'Refeições', desc: 'Mostre o que você come' },
        { title: 'Treinos', desc: 'Compartilhe suas sessões' },
        { title: 'Motivação', desc: 'Inspire a comunidade' },
        { title: 'Conquistas', desc: 'Celebre o sucesso' },
        { title: 'Desafios', desc: 'Compita com amigos' }
      ]
    },
    challenges: {
      title: 'Desafios em grupo',
      subtitle: 'para vencer juntos',
      description: 'Participe de desafios com amigos e alcance seus objetivos mais rápido.',
      challengeTitle: 'Desafio Verão 2026',
      goal: 'Objetivo: -5kg em 30 dias',
      participants: 'participantes',
      daysLeft: 'dias restantes',
      totalLost: 'kg perdidos pelo grupo',
      names: ['João', 'Maria', 'Pedro', 'Ana', 'Lucas', 'Julia', 'Gabriel', 'Beatriz']
    },
    mealPlan: {
      title: 'Plano Alimentar IA',
      subtitle: 'personalizado para si',
      description: 'A inteligência artificial gera um plano alimentar semanal completo, com ingredientes, quantidades e instruções de preparação adaptados aos seus objetivos.',
      badge: 'Premium',
      features: [
        'Plano semanal de 7 dias',
        'Quantidades precisas por ingrediente',
        'Instruções de preparação',
        'Personalizado para alergias e preferências'
      ],
      cta: 'Experimentar Plano IA',
      header: 'Plano Alimentar IA',
      headerSub: 'Recurso Premium',
      dailyAvg: 'Média diária',
      balanced: 'Equilibrado',
      avgCal: '1497',
      calUnit: 'kcal/dia',
      prepLabel: 'PREPARAÇÃO',
      sample: {
        day: 'Segunda',
        totalCal: '1550 kcal',
        meals: [
          { type: 'CAFÉ DA MANHÃ', name: 'Porridge de aveia', cal: '300', ingredients: '50g flocos de aveia · 200ml leite de amêndoas · 1 banana', preparation: 'Cozinhar os flocos de aveia no leite de amêndoas até ficar cremoso. Servir com banana fatiada por cima.', emoji: '🍌' },
          { type: 'LANCHE', name: 'Maçã com manteiga de amendoim', cal: '200', ingredients: '1 maçã · 1 colher manteiga de amendoim', preparation: 'Cortar a maçã em fatias e barrar a manteiga de amendoim. Servir fresco.', emoji: '🍏' },
          { type: 'ALMOÇO', name: 'Salada de quinoa', cal: '450', ingredients: '80g quinoa · 100g frango · tomates cereja · pepinos', preparation: 'Cozinhar a quinoa e o frango. Misturar com os legumes cortados e temperar com azeite e limão.', emoji: '🥗' }
        ]
      }
    },
    shoppingList: {
      title: 'Lista de Compras IA',
      subtitle: 'do seu Plano Alimentar',
      description: 'Gere automaticamente a lista de compras do seu plano alimentar. Escolha quantos dias e marque os ingredientes enquanto faz compras.',
      badge: 'Premium',
      forHowManyDays: 'Para quantos dias?',
      dayOptions: ['1 dia', '2 dias', '3 dias', 'Semana'],
      weekDays: 'Segunda, Terça, Quarta, Quinta, Sexta, Sábado, Domingo',
      progress: '0/10',
      categories: {
        produce: 'Frutas e Vegetais',
        proteins: 'Carne e Peixe',
        dairy: 'Laticínios e Ovos',
        grains: 'Cereais e Legumes',
        other: 'Outros'
      },
      sampleItems: [
        '1 banana',
        '50g fruta mista',
        '100g salada mista',
        '200g brócolis',
        '1 colher limão'
      ],
      proteinItems: [
        '150g peito de frango',
        '150g salmão'
      ],
      dayLabel: 'Segunda',
      cta: 'Gerar sua lista'
    },
    chefAi: {
      title: 'Chef IA',
      subtitle: 'seu nutricionista pessoal',
      description: 'Gere receitas saudáveis personalizadas com inteligência artificial. Você também pode compartilhá-las com a comunidade!',
      recipe: {
        name: 'Frango Grelhado Mediterrâneo',
        time: '25 min',
        calories: '380 kcal',
        protein: '42g proteína',
        ingredients: ['Peito de frango', 'Tomate cereja', 'Azeitonas', 'Feta', 'Rúcula', 'Azeite'],
        shareText: 'Compartilhar com a comunidade'
      },
      badge: 'Gerado por IA',
      tryIt: 'Experimentar Chef IA'
    },
    gpsTracker: {
      title: 'GPS Tracker',
      subtitle: 'para suas caminhadas',
      description: 'Rastreie suas rotas, monitore distância, tempo e calorias queimadas. Compartilhe suas caminhadas com a comunidade!',
      walkTitle: 'Caminhada',
      distance: 'Distância',
      time: 'Tempo',
      calories: 'Calorias',
      pace: 'Ritmo',
      pause: 'Pausar',
      stop: 'Parar',
      startWalk: 'Iniciar caminhada',
      shareWalk: 'Compartilhar rota',
      live: 'AO VIVO',
      buddiesLabel: 'Treinando agora',
      buddies: [
        { name: 'Pedro', km: '2.1', min: '18' },
        { name: 'Sofia', km: '3.4', min: '26' },
        { name: 'Bruno', km: '1.8', min: '14' }
      ],
      cameraLabel: 'Foto',
      micLabel: 'Voz'
    },
    weightTracking: {
      title: 'Rastreie seu peso',
      subtitle: 'com precisão científica',
      description: 'A curva biológica mostra o caminho ideal. Pontos abaixo da curva são vitórias, os acima são oportunidades de melhoria.',
      start: 'Início',
      goal: 'Objetivo',
      current: 'Peso atual',
      lost: 'Perdidos',
      toGo: 'Faltam',
      monthsLabel: 'meses'
    },
    features: {
      title: 'Tudo que você precisa',
      items: [
        { icon: 'Scale', title: 'Rastreio de Peso', desc: 'Monitore o progresso com gráficos' },
        { icon: 'Users', title: 'Comunidade Social', desc: 'Compartilhe e inspire outros' },
        { icon: 'Trophy', title: 'Desafios em Grupo', desc: 'Compita com amigos' },
        { icon: 'ChefHat', title: 'Chef IA', desc: 'Receitas personalizadas com IA' },
        { icon: 'MapPin', title: 'GPS Tracker', desc: 'Rastreie suas caminhadas' },
        { icon: 'Sparkles', title: 'Transformação IA', desc: 'Visualize seu objetivo' }
      ]
    },
    stats: {
      users: 'Usuários ativos',
      weight: 'Kg perdidos',
      challenges: 'Desafios completados'
    },
    cta: {
      title: 'Comece sua jornada hoje',
      subtitle: '100% Grátis',
      description: 'Todas as funcionalidades são completamente gratuitas. Sem custos ocultos, sem assinatura obrigatória.',
      button: 'Comece Grátis Agora',
      noCard: 'Sem cartão de crédito • Grátis para sempre'
    },
    appStores: {
      comingSoon: 'Em breve no',
      downloadOn: 'Baixar na',
      appStore: 'App Store',
      playStore: 'Google Play'
    },
    footer: {
      privacy: 'Privacidade',
      terms: 'Termos',
      support: 'Suporte',
      rights: 'Todos os direitos reservados'
    },
    supportCard: {
      title: 'Precisa de ajuda?',
      description: 'Escreva-nos para pedidos de informações ou suporte.',
      cta: 'Fale conosco'
    },
    leaderboard: {
      title: 'Ranking Nacional',
      subtitle: 'em tempo real',
      description: 'Suba no ranking do seu país toda semana. Compita em km, peso perdido e atividades concluídas. Receberá notificações push quando estiver no pódio.',
      bullets: [
        'Top 1 nacional toda segunda',
        'Ranking por país, cidade e amigos',
        'Notificações push quando te ultrapassam',
        'Histórico semanal e mensal'
      ],
      tag: 'Compete com o seu país'
    },
    languages: {
      title: 'Disponível em 6 idiomas',
      subtitle: 'comunidade global',
      description: 'Slimmy fala a sua língua. O app, as notificações e até o chat de suporte se adaptam automaticamente.',
      list: [
        { code: 'it', flag: '🇮🇹', name: 'Italiano' },
        { code: 'en', flag: '🇬🇧', name: 'English' },
        { code: 'es', flag: '🇪🇸', name: 'Español' },
        { code: 'pt', flag: '🇧🇷', name: 'Português' },
        { code: 'de', flag: '🇩🇪', name: 'Deutsch' },
        { code: 'fr', flag: '🇫🇷', name: 'Français' }
      ],
      ctaTag: 'Mude de idioma quando quiser'
    },
    pricing: {
      title: 'Comece grátis,',
      subtitle: 'libere o Premium quando quiser',
      description: 'Todas as funcionalidades principais são gratuitas. O Premium adiciona IA ilimitada, planos alimentares, rankings completos e mais.',
      free: {
        name: 'Free',
        price: 'R$0',
        period: 'para sempre',
        features: [
          'Tracker de peso ilimitado',
          'Desafios da comunidade',
          'Caminhada GPS',
          'Feed social com fotos e vídeos',
          'Walkie-talkie com parceiros de desafio',
          'Fotos e vídeos AO VIVO durante o treino'
        ],
        cta: 'Começar grátis'
      },
      premium: {
        name: 'Premium',
        price: 'R$24,90',
        period: '/ mês',
        badge: 'Recomendado',
        features: [
          'Tudo do plano Free',
          'AI Body Transformation semanal',
          'Planos alimentares com IA personalizados',
          'Chef IA para receitas sob medida',
          'Rankings detalhados e histórico',
          'Estatísticas avançadas'
        ],
        cta: 'Experimentar Premium'
      },
      lifetimeBadge: 'Primeiros 1.000 utilizadores: Premium grátis para a vida 🎁'
    },
    walkieTalkie: {
      title: 'Walkie-talkie',
      subtitle: 'durante o treino',
      description: 'Fale em tempo real com um parceiro de treino. Pressione e segure para falar, solte para ouvir. Perfeito para caminhadas, corridas e bicicleta.',
      bullets: [
        'Áudio ao vivo de baixa latência',
        'Push-to-talk com um só dedo',
        'Funciona com a tela bloqueada',
        'Sincronizado com o tracker GPS'
      ],
      hint: 'Segure para falar',
      live: 'AO VIVO',
      premium: 'Apenas com parceiros de desafio ativos'
    },
    shareWorkout: {
      title: 'Fotos e vídeos AO VIVO',
      subtitle: 'durante o treino',
      description: 'Enquanto caminha ou corre, pode tirar fotos e vídeos curtos diretamente do tracker e partilhá-los na hora com a comunidade. No fim do treino, o post é completado automaticamente com o mapa GPS e as estatísticas.',
      bullets: [
        'Fotos em tempo real durante o exercício',
        'Vídeos curtos (máx. 60 seg) em movimento',
        'Mapa GPS da rota anexado ao post',
        'Estatísticas calculadas automaticamente'
      ],
      stats: { distance: 'Distância', time: 'Tempo', calories: 'Calorias', pace: 'Pace' },
      caption: '5km na subida 🏃‍♂️ Novo recorde pessoal!',
      tag: 'Partilha ao vivo',
      feedMock: {
        title: 'Feed',
        tabs: { all: 'Todos', following: 'Tribo', challenges: 'Desafios' },
        visibility: 'Público',
        bottomNav: { home: 'Início', feed: 'Feed', challenges: 'Desafios', profile: 'Perfil' },
        categories: { motivation: 'Motivação', food: 'Comida', workout: 'Treino', walk: 'Caminhada' },
        posts: [
          { name: 'Beatriz', isPremium: false, category: 'food',    timeAgo: 'há 2 h',      caption: 'Quando dizes "só um prato" mas já sabes que vais comer 4 😂', photoIdx: 1 },
          { name: 'Tiago',   isPremium: true,  category: 'workout',    timeAgo: 'há 39 min',   caption: 'Trekking na neve com o Max 🏔️',                       photoIdx: 0, videoIdx: 0 },
          { name: 'Inês',    isPremium: false, category: 'walk',    timeAgo: 'ontem',       caption: 'Caminhada de 3 km na montanha 🏔️',                            photoIdx: 3 },
          { name: 'Diogo',   isPremium: true,  category: 'workout', timeAgo: 'há 1 dia',    caption: 'Um pouco de movimento hoje também 🚶‍♂️',                                     photoIdx: 4, videoIdx: 1 },
        ]
      }
    },
    nav: {
      home: 'Início',
      feed: 'Feed',
      challenges: 'Desafios',
      profile: 'Perfil'
    }
  },
  de: {
    hero: {
      title: 'Abnehmen und',
      titleLine2: 'in Form bleiben',
      subtitle: 'mit der Community',
      description: 'Verfolge deinen Fortschritt, nimm an Challenges mit Freunden teil und erreiche deine Ziele mit der Unterstützung einer motivierten Community.',
      cta: 'Kostenlos starten',
      login: 'Anmelden'
    },
    aiTransform: {
      title: 'Visualisiere deine Zukunft',
      subtitle: 'mit KI',
      description: 'Künstliche Intelligenz zeigt dir, wie du aussehen könntest, wenn du dein Ziel erreichst.',
      stat: '+40%',
      statText: 'höhere Chance, dein Ziel zu erreichen, wenn du es visualisierst',
      before: 'Vorher',
      after: 'Nachher',
      dragHint: 'Ziehen um die Transformation zu sehen'
    },
    social: {
      title: 'Teile jeden Moment',
      subtitle: 'deiner Reise',
      description: 'Die Community unterstützt dich bei jedem Schritt. Teile deinen Fortschritt und inspiriere andere.',
      types: [
        { title: 'Gewicht', desc: 'Feiere jeden Meilenstein' },
        { title: 'Mahlzeiten', desc: 'Zeige was du isst' },
        { title: 'Training', desc: 'Teile deine Sessions' },
        { title: 'Motivation', desc: 'Inspiriere die Community' },
        { title: 'Erfolge', desc: 'Feiere den Erfolg' },
        { title: 'Challenges', desc: 'Konkurriere mit Freunden' }
      ]
    },
    challenges: {
      title: 'Gruppen-Challenges',
      subtitle: 'gemeinsam gewinnen',
      description: 'Nimm an Challenges mit Freunden teil und erreiche deine Ziele schneller.',
      challengeTitle: 'Sommer Challenge 2026',
      goal: 'Ziel: -5kg in 30 Tagen',
      participants: 'Teilnehmer',
      daysLeft: 'Tage übrig',
      totalLost: 'kg von der Gruppe verloren',
      names: ['Maximilian', 'Sophie', 'Felix', 'Emma', 'Leon', 'Mia', 'Paul', 'Hannah']
    },
    mealPlan: {
      title: 'KI-Ernährungsplan',
      subtitle: 'personalisiert für dich',
      description: 'Die künstliche Intelligenz erstellt einen kompletten wöchentlichen Ernährungsplan mit Zutaten, Mengen und Zubereitungsanleitungen, abgestimmt auf deine Ziele.',
      badge: 'Premium',
      features: [
        '7-Tage-Wochenplan',
        'Genaue Mengen pro Zutat',
        'Zubereitungsanleitung',
        'Angepasst an Allergien und Vorlieben'
      ],
      cta: 'KI-Plan testen',
      header: 'KI-Ernährungsplan',
      headerSub: 'Premium-Funktion',
      dailyAvg: 'Tagesdurchschnitt',
      balanced: 'Ausgewogen',
      avgCal: '1497',
      calUnit: 'kcal/Tag',
      prepLabel: 'ZUBEREITUNG',
      sample: {
        day: 'Montag',
        totalCal: '1550 kcal',
        meals: [
          { type: 'FRÜHSTÜCK', name: 'Haferbrei', cal: '300', ingredients: '50g Haferflocken · 200ml Mandelmilch · 1 Banane', preparation: 'Haferflocken in Mandelmilch kochen, bis eine cremige Konsistenz entsteht. Mit Bananenscheiben servieren.', emoji: '🍌' },
          { type: 'SNACK', name: 'Apfel mit Erdnussbutter', cal: '200', ingredients: '1 Apfel · 1 EL Erdnussbutter', preparation: 'Den Apfel in Scheiben schneiden und mit Erdnussbutter bestreichen. Frisch servieren.', emoji: '🍏' },
          { type: 'MITTAGESSEN', name: 'Quinoa-Salat', cal: '450', ingredients: '80g Quinoa · 100g Hähnchen · Kirschtomaten · Gurken', preparation: 'Quinoa und Hähnchen kochen. Mit gewürfeltem Gemüse mischen und mit Öl und Zitrone anmachen.', emoji: '🥗' }
        ]
      }
    },
    shoppingList: {
      title: 'KI-Einkaufsliste',
      subtitle: 'aus deinem Ernährungsplan',
      description: 'Erstelle automatisch deine Einkaufsliste aus deinem Ernährungsplan. Wähle wie viele Tage und hake Zutaten beim Einkaufen ab.',
      badge: 'Premium',
      forHowManyDays: 'Für wie viele Tage?',
      dayOptions: ['1 Tag', '2 Tage', '3 Tage', 'Woche'],
      weekDays: 'Montag, Dienstag, Mittwoch, Donnerstag, Freitag, Samstag, Sonntag',
      progress: '0/10',
      categories: {
        produce: 'Obst & Gemüse',
        proteins: 'Fleisch & Fisch',
        dairy: 'Milch & Eier',
        grains: 'Getreide & Hülsenfrüchte',
        other: 'Sonstiges'
      },
      sampleItems: [
        '1 Banane',
        '50g gemischtes Obst',
        '100g gemischter Salat',
        '200g Brokkoli',
        '1 EL Zitrone'
      ],
      proteinItems: [
        '150g Hähnchenbrust',
        '150g Lachs'
      ],
      dayLabel: 'Montag',
      cta: 'Liste generieren'
    },
    chefAi: {
      title: 'KI-Koch',
      subtitle: 'dein persönlicher Ernährungsberater',
      description: 'Erstelle personalisierte gesunde Rezepte mit künstlicher Intelligenz. Du kannst sie auch mit der Community teilen!',
      recipe: {
        name: 'Mediterranes Grillhähnchen',
        time: '25 Min',
        calories: '380 kcal',
        protein: '42g Protein',
        ingredients: ['Hähnchenbrust', 'Kirschtomaten', 'Oliven', 'Feta', 'Rucola', 'Olivenöl'],
        shareText: 'Mit Community teilen'
      },
      badge: 'KI-generiert',
      tryIt: 'KI-Koch testen'
    },
    gpsTracker: {
      title: 'GPS-Tracker',
      subtitle: 'für deine Spaziergänge',
      description: 'Verfolge deine Routen, überwache Entfernung, Zeit und verbrannte Kalorien. Teile deine Spaziergänge mit der Community!',
      walkTitle: 'Spaziergang',
      distance: 'Entfernung',
      time: 'Zeit',
      calories: 'Kalorien',
      pace: 'Tempo',
      pause: 'Pause',
      stop: 'Stopp',
      startWalk: 'Spaziergang starten',
      shareWalk: 'Route teilen',
      live: 'LIVE',
      buddiesLabel: 'Trainieren gerade',
      buddies: [
        { name: 'Lukas', km: '2.1', min: '18' },
        { name: 'Anna', km: '3.4', min: '26' },
        { name: 'Felix', km: '1.8', min: '14' }
      ],
      cameraLabel: 'Foto',
      micLabel: 'Sprache'
    },
    weightTracking: {
      title: 'Verfolge dein Gewicht',
      subtitle: 'mit wissenschaftlicher Präzision',
      description: 'Die biologische Kurve zeigt dir den idealen Weg. Punkte unter der Kurve sind Siege, die darüber sind Verbesserungsmöglichkeiten.',
      start: 'Start',
      goal: 'Ziel',
      current: 'Aktuelles Gewicht',
      lost: 'Verloren',
      toGo: 'Noch',
      monthsLabel: 'Monate'
    },
    features: {
      title: 'Alles was du brauchst',
      items: [
        { icon: 'Scale', title: 'Gewichtstracking', desc: 'Fortschritt mit Diagrammen verfolgen' },
        { icon: 'Users', title: 'Soziale Community', desc: 'Teilen und andere inspirieren' },
        { icon: 'Trophy', title: 'Gruppen-Challenges', desc: 'Mit Freunden konkurrieren' },
        { icon: 'ChefHat', title: 'KI-Koch', desc: 'KI-personalisierte Rezepte' },
        { icon: 'MapPin', title: 'GPS-Tracker', desc: 'Spaziergänge verfolgen' },
        { icon: 'Sparkles', title: 'KI-Transformation', desc: 'Dein Ziel visualisieren' }
      ]
    },
    stats: {
      users: 'Aktive Nutzer',
      weight: 'Kg verloren',
      challenges: 'Challenges abgeschlossen'
    },
    cta: {
      title: 'Starte deine Reise heute',
      subtitle: '100% Kostenlos',
      description: 'Alle Funktionen sind völlig kostenlos. Keine versteckten Kosten, kein Pflichtabonnement.',
      button: 'Jetzt Kostenlos Starten',
      noCard: 'Keine Kreditkarte erforderlich • Für immer kostenlos'
    },
    appStores: {
      comingSoon: 'Bald verfügbar auf',
      downloadOn: 'Laden im',
      appStore: 'App Store',
      playStore: 'Google Play'
    },
    footer: {
      privacy: 'Datenschutz',
      terms: 'AGB',
      support: 'Support',
      rights: 'Alle Rechte vorbehalten'
    },
    supportCard: {
      title: 'Brauchst du Hilfe?',
      description: 'Schreib uns für Informationen oder Support-Anfragen.',
      cta: 'Kontaktiere uns'
    },
    leaderboard: {
      title: 'Nationale Rangliste',
      subtitle: 'in Echtzeit',
      description: 'Steige jede Woche in der Rangliste deines Landes auf. Konkurriere in km, verlorenem Gewicht und absolvierten Aktivitäten. Du erhältst Push-Benachrichtigungen, wenn du auf dem Podium stehst.',
      bullets: [
        'Nationale Top 1 jeden Montag',
        'Ranking nach Land, Stadt und Freunden',
        'Push-Benachrichtigungen beim Überholt-werden',
        'Wöchentliche und monatliche Historie'
      ],
      tag: 'Konkurriere mit deinem Land'
    },
    languages: {
      title: 'In 6 Sprachen verfügbar',
      subtitle: 'globale Community',
      description: 'Slimmy spricht deine Sprache. Die App, Benachrichtigungen und sogar der Support-Chat passen sich automatisch an.',
      list: [
        { code: 'it', flag: '🇮🇹', name: 'Italiano' },
        { code: 'en', flag: '🇬🇧', name: 'English' },
        { code: 'es', flag: '🇪🇸', name: 'Español' },
        { code: 'pt', flag: '🇧🇷', name: 'Português' },
        { code: 'de', flag: '🇩🇪', name: 'Deutsch' },
        { code: 'fr', flag: '🇫🇷', name: 'Français' }
      ],
      ctaTag: 'Sprache jederzeit wechseln'
    },
    pricing: {
      title: 'Kostenlos starten,',
      subtitle: 'Premium freischalten, wann du willst',
      description: 'Alle Kernfunktionen sind kostenlos. Premium fügt unbegrenzte KI, Ernährungspläne, vollständige Ranglisten und mehr hinzu.',
      free: {
        name: 'Free',
        price: '0€',
        period: 'für immer',
        features: [
          'Unbegrenzter Gewichts-Tracker',
          'Community-Challenges',
          'GPS-Spaziergang',
          'Social Feed mit Fotos und Videos',
          'Walkie-talkie mit Challenge-Partnern',
          'LIVE-Fotos und Videos beim Training'
        ],
        cta: 'Kostenlos starten'
      },
      premium: {
        name: 'Premium',
        price: '4,99€',
        period: '/ Monat',
        badge: 'Empfohlen',
        features: [
          'Alles aus dem Free-Tarif',
          'Wöchentliche AI Body Transformation',
          'Personalisierte KI-Ernährungspläne',
          'KI-Koch für maßgeschneiderte Rezepte',
          'Detaillierte Ranglisten & Historie',
          'Erweiterte Statistiken'
        ],
        cta: 'Premium testen'
      },
      lifetimeBadge: 'Erste 1.000 Nutzer: Lebenslanges Premium gratis 🎁'
    },
    walkieTalkie: {
      title: 'Walkie-talkie',
      subtitle: 'während des Trainings',
      description: 'Sprich in Echtzeit mit einem Trainingspartner. Drücke und halte, um zu sprechen, lass los, um zuzuhören. Perfekt für Wanderungen, Läufe und Radtouren.',
      bullets: [
        'Live-Audio mit geringer Latenz',
        'Push-to-Talk mit einem Finger',
        'Funktioniert auch bei gesperrtem Bildschirm',
        'Mit dem GPS-Tracker synchronisiert'
      ],
      hint: 'Halten zum Sprechen',
      live: 'LIVE',
      premium: 'Nur mit aktiven Challenge-Partnern'
    },
    shareWorkout: {
      title: 'Fotos & Videos LIVE',
      subtitle: 'während des Trainings',
      description: 'Während du gehst oder läufst, kannst du direkt aus dem Tracker Fotos und kurze Videos aufnehmen und sofort mit der Community teilen. Am Ende des Trainings wird der Post automatisch mit GPS-Karte und Statistiken vervollständigt.',
      bullets: [
        'Echtzeit-Fotos während der Aktivität',
        'Kurze Videos (max. 60 Sek) in Bewegung',
        'GPS-Routenkarte am Post angehängt',
        'Statistiken automatisch berechnet'
      ],
      stats: { distance: 'Distanz', time: 'Zeit', calories: 'Kalorien', pace: 'Tempo' },
      caption: '5 km Hügellauf 🏃‍♂️ Neuer persönlicher Rekord!',
      tag: 'Live teilen',
      feedMock: {
        title: 'Feed',
        tabs: { all: 'Alle', following: 'Tribe', challenges: 'Challenges' },
        visibility: 'Öffentlich',
        bottomNav: { home: 'Start', feed: 'Feed', challenges: 'Challenges', profile: 'Profil' },
        categories: { motivation: 'Motivation', food: 'Essen', workout: 'Training', walk: 'Gehen' },
        posts: [
          { name: 'Emma',  isPremium: false, category: 'food',    timeAgo: 'vor 2 Std',  caption: 'Wenn du "nur ein Teller" sagst, aber 4 isst 😂',                  photoIdx: 1 },
          { name: 'Lukas', isPremium: true,  category: 'workout',    timeAgo: 'vor 39 Min', caption: 'Wandern im Schnee mit Max 🏔️',                                    photoIdx: 0, videoIdx: 0 },
          { name: 'Mia',   isPremium: false, category: 'walk',    timeAgo: 'gestern',    caption: '3 km Bergwanderung 🏔️',                                           photoIdx: 3 },
          { name: 'Tim',   isPremium: true,  category: 'workout', timeAgo: 'vor 1 Tag',  caption: 'Auch heute etwas Bewegung 🚶‍♂️',                                 photoIdx: 4, videoIdx: 1 },
        ]
      }
    },
    nav: {
      home: 'Start',
      feed: 'Feed',
      challenges: 'Challenges',
      profile: 'Profil'
    }
  },
  fr: {
    hero: {
      title: 'Perdez du poids et',
      titleLine2: 'restez en forme',
      subtitle: 'avec la communauté',
      description: 'Suivez vos progrès, participez à des défis avec des amis et atteignez vos objectifs avec le soutien d\'une communauté motivée.',
      cta: 'Commencer Gratuit',
      login: 'Connexion'
    },
    aiTransform: {
      title: 'Visualisez votre futur',
      subtitle: 'avec l\'IA',
      description: 'L\'intelligence artificielle vous montre à quoi vous pourriez ressembler en atteignant votre objectif.',
      stat: '+40%',
      statText: 'plus de chances d\'atteindre votre objectif en le visualisant',
      before: 'Avant',
      after: 'Après',
      dragHint: 'Glissez pour voir la transformation'
    },
    social: {
      title: 'Partagez chaque moment',
      subtitle: 'de votre parcours',
      description: 'La communauté vous soutient à chaque étape. Partagez vos progrès et inspirez les autres.',
      types: [
        { title: 'Poids', desc: 'Célébrez chaque étape' },
        { title: 'Repas', desc: 'Montrez ce que vous mangez' },
        { title: 'Entraînements', desc: 'Partagez vos séances' },
        { title: 'Motivation', desc: 'Inspirez la communauté' },
        { title: 'Objectifs', desc: 'Célébrez le succès' },
        { title: 'Défis', desc: 'Rivalisez avec des amis' }
      ]
    },
    challenges: {
      title: 'Défis de groupe',
      subtitle: 'pour gagner ensemble',
      description: 'Rejoignez des défis avec des amis et atteignez vos objectifs plus rapidement.',
      challengeTitle: 'Défi Été 2026',
      goal: 'Objectif: -5kg en 30 jours',
      participants: 'participants',
      daysLeft: 'jours restants',
      totalLost: 'kg perdus par le groupe',
      names: ['Lucas', 'Chloé', 'Hugo', 'Emma', 'Louis', 'Léa', 'Nathan', 'Manon']
    },
    mealPlan: {
      title: 'Plan Alimentaire IA',
      subtitle: 'personnalisé pour vous',
      description: 'L\'intelligence artificielle génère un plan alimentaire hebdomadaire complet, avec ingrédients, quantités et instructions de préparation adaptés à vos objectifs.',
      badge: 'Premium',
      features: [
        'Plan hebdomadaire 7 jours',
        'Quantités précises par ingrédient',
        'Instructions de préparation',
        'Personnalisé selon allergies et préférences'
      ],
      cta: 'Essayer le Plan IA',
      header: 'Plan Alimentaire IA',
      headerSub: 'Fonctionnalité Premium',
      dailyAvg: 'Moyenne quotidienne',
      balanced: 'Équilibré',
      avgCal: '1497',
      calUnit: 'kcal/jour',
      prepLabel: 'PRÉPARATION',
      sample: {
        day: 'Lundi',
        totalCal: '1550 kcal',
        meals: [
          { type: 'PETIT-DÉJEUNER', name: 'Porridge d\'avoine', cal: '300', ingredients: '50g flocons d\'avoine · 200ml lait d\'amande · 1 banane', preparation: 'Cuire les flocons d\'avoine dans le lait d\'amande jusqu\'à consistance crémeuse. Servir avec la banane en rondelles.', emoji: '🍌' },
          { type: 'COLLATION', name: 'Pomme au beurre de cacahuète', cal: '200', ingredients: '1 pomme · 1 c.à.s. beurre de cacahuète', preparation: 'Couper la pomme en tranches et tartiner le beurre de cacahuète. Servir frais.', emoji: '🍏' },
          { type: 'DÉJEUNER', name: 'Salade de quinoa', cal: '450', ingredients: '80g quinoa · 100g poulet · tomates cerises · concombres', preparation: 'Cuire le quinoa et le poulet. Mélanger avec les légumes coupés et assaisonner d\'huile et de citron.', emoji: '🥗' }
        ]
      }
    },
    shoppingList: {
      title: 'Liste de Courses IA',
      subtitle: 'de votre Plan Alimentaire',
      description: 'Générez automatiquement votre liste de courses depuis votre plan alimentaire. Choisissez combien de jours et cochez les ingrédients en faisant vos courses.',
      badge: 'Premium',
      forHowManyDays: 'Pour combien de jours?',
      dayOptions: ['1 jour', '2 jours', '3 jours', 'Semaine'],
      weekDays: 'Lundi, Mardi, Mercredi, Jeudi, Vendredi, Samedi, Dimanche',
      progress: '0/10',
      categories: {
        produce: 'Fruits & Légumes',
        proteins: 'Viande & Poisson',
        dairy: 'Produits Laitiers & Œufs',
        grains: 'Céréales & Légumineuses',
        other: 'Autres'
      },
      sampleItems: [
        '1 banane',
        '50g fruits mélangés',
        '100g salade mixte',
        '200g brocolis',
        '1 c.à.s. citron'
      ],
      proteinItems: [
        '150g blanc de poulet',
        '150g saumon'
      ],
      dayLabel: 'Lundi',
      cta: 'Générer votre liste'
    },
    chefAi: {
      title: 'Chef IA',
      subtitle: 'votre nutritionniste personnel',
      description: 'Générez des recettes saines personnalisées avec l\'intelligence artificielle. Vous pouvez aussi les partager avec la communauté !',
      recipe: {
        name: 'Poulet Grillé Méditerranéen',
        time: '25 min',
        calories: '380 kcal',
        protein: '42g protéines',
        ingredients: ['Blanc de poulet', 'Tomates cerises', 'Olives', 'Feta', 'Roquette', 'Huile d\'olive'],
        shareText: 'Partager avec la communauté'
      },
      badge: 'Généré par IA',
      tryIt: 'Essayer Chef IA'
    },
    gpsTracker: {
      title: 'GPS Tracker',
      subtitle: 'pour vos marches',
      description: 'Suivez vos itinéraires, surveillez la distance, le temps et les calories brûlées. Partagez vos marches avec la communauté!',
      walkTitle: 'Marche',
      distance: 'Distance',
      time: 'Temps',
      calories: 'Calories',
      pace: 'Rythme',
      pause: 'Pause',
      stop: 'Arrêter',
      startWalk: 'Démarrer la marche',
      shareWalk: 'Partager l\'itinéraire',
      live: 'EN DIRECT',
      buddiesLabel: 'En entraînement',
      buddies: [
        { name: 'Antoine', km: '2.1', min: '18' },
        { name: 'Camille', km: '3.4', min: '26' },
        { name: 'Hugo', km: '1.8', min: '14' }
      ],
      cameraLabel: 'Photo',
      micLabel: 'Voix'
    },
    weightTracking: {
      title: 'Suivez votre poids',
      subtitle: 'avec précision scientifique',
      description: 'La courbe biologique vous montre le chemin idéal. Les points sous la courbe sont des victoires, ceux au-dessus sont des opportunités d\'amélioration.',
      start: 'Début',
      goal: 'Objectif',
      current: 'Poids actuel',
      lost: 'Perdus',
      toGo: 'Reste',
      monthsLabel: 'mois'
    },
    features: {
      title: 'Tout ce dont vous avez besoin',
      items: [
        { icon: 'Scale', title: 'Suivi du Poids', desc: 'Surveillez les progrès avec des graphiques' },
        { icon: 'Users', title: 'Communauté Sociale', desc: 'Partagez et inspirez les autres' },
        { icon: 'Trophy', title: 'Défis de Groupe', desc: 'Rivalisez avec des amis' },
        { icon: 'ChefHat', title: 'Chef IA', desc: 'Recettes personnalisées par IA' },
        { icon: 'MapPin', title: 'GPS Tracker', desc: 'Suivez vos marches' },
        { icon: 'Sparkles', title: 'Transformation IA', desc: 'Visualisez votre objectif' }
      ]
    },
    stats: {
      users: 'Utilisateurs actifs',
      weight: 'Kg perdus',
      challenges: 'Défis complétés'
    },
    cta: {
      title: 'Commencez votre parcours aujourd\'hui',
      subtitle: '100% Gratuit',
      description: 'Toutes les fonctionnalités sont entièrement gratuites. Pas de coûts cachés, pas d\'abonnement obligatoire.',
      button: 'Commencer Gratuit Maintenant',
      noCard: 'Aucune carte de crédit requise • Gratuit pour toujours'
    },
    appStores: {
      comingSoon: 'Bientôt disponible sur',
      downloadOn: 'Télécharger dans',
      appStore: 'App Store',
      playStore: 'Google Play'
    },
    footer: {
      privacy: 'Confidentialité',
      terms: 'Conditions',
      support: 'Assistance',
      rights: 'Tous droits réservés'
    },
    supportCard: {
      title: 'Besoin d\'aide ?',
      description: 'Écrivez-nous pour toute demande d\'informations ou d\'assistance.',
      cta: 'Contactez-nous'
    },
    leaderboard: {
      title: 'Classement National',
      subtitle: 'en temps réel',
      description: 'Gravissez le classement de votre pays chaque semaine. Concourez en km, poids perdu et activités terminées. Vous recevrez des notifications push lorsque vous êtes sur le podium.',
      bullets: [
        'Top 1 national chaque lundi',
        'Classement par pays, ville et amis',
        'Notifications push lors d\'un dépassement',
        'Historique hebdomadaire et mensuel'
      ],
      tag: 'Concourez avec votre pays'
    },
    languages: {
      title: 'Disponible en 6 langues',
      subtitle: 'communauté mondiale',
      description: 'Slimmy parle votre langue. L\'app, les notifications et même le chat d\'assistance s\'adaptent automatiquement.',
      list: [
        { code: 'it', flag: '🇮🇹', name: 'Italiano' },
        { code: 'en', flag: '🇬🇧', name: 'English' },
        { code: 'es', flag: '🇪🇸', name: 'Español' },
        { code: 'pt', flag: '🇧🇷', name: 'Português' },
        { code: 'de', flag: '🇩🇪', name: 'Deutsch' },
        { code: 'fr', flag: '🇫🇷', name: 'Français' }
      ],
      ctaTag: 'Changez de langue quand vous voulez'
    },
    pricing: {
      title: 'Commencez gratuitement,',
      subtitle: 'débloquez Premium quand vous voulez',
      description: 'Toutes les fonctionnalités essentielles sont gratuites. Premium ajoute IA illimitée, plans repas, classements complets et plus.',
      free: {
        name: 'Free',
        price: '0€',
        period: 'pour toujours',
        features: [
          'Tracker poids illimité',
          'Défis de la communauté',
          'Marche GPS',
          'Fil social avec photos et vidéos',
          'Walkie-talkie avec partenaires de défi',
          'Photos et vidéos EN DIRECT pendant l\'entraînement'
        ],
        cta: 'Commencer gratuitement'
      },
      premium: {
        name: 'Premium',
        price: '4,99€',
        period: '/ mois',
        badge: 'Recommandé',
        features: [
          'Tout le plan Free',
          'AI Body Transformation hebdomadaire',
          'Plans repas IA personnalisés',
          'Chef IA pour recettes sur mesure',
          'Classements détaillés et historique',
          'Statistiques avancées'
        ],
        cta: 'Essayer Premium'
      },
      lifetimeBadge: '1 000 premiers utilisateurs : Premium à vie gratuit 🎁'
    },
    walkieTalkie: {
      title: 'Walkie-talkie',
      subtitle: 'pendant l\'entraînement',
      description: 'Parlez en temps réel avec un partenaire d\'entraînement. Maintenez pour parler, relâchez pour écouter. Parfait pour la marche, la course et le vélo.',
      bullets: [
        'Audio live à faible latence',
        'Push-to-talk d\'un seul doigt',
        'Fonctionne même écran verrouillé',
        'Synchronisé avec le tracker GPS'
      ],
      hint: 'Maintenez pour parler',
      live: 'LIVE',
      premium: 'Avec partenaires de défi actifs uniquement'
    },
    shareWorkout: {
      title: 'Photos & vidéos EN DIRECT',
      subtitle: 'pendant l\'entraînement',
      description: 'Pendant que vous marchez ou courez, vous pouvez prendre des photos et de courtes vidéos directement depuis le tracker et les partager instantanément avec la communauté. À la fin de l\'entraînement, le post est complété automatiquement avec la carte GPS et les statistiques.',
      bullets: [
        'Photos en temps réel pendant l\'exercice',
        'Vidéos courtes (max 60 sec) en mouvement',
        'Carte GPS du parcours jointe au post',
        'Statistiques calculées automatiquement'
      ],
      stats: { distance: 'Distance', time: 'Temps', calories: 'Calories', pace: 'Allure' },
      caption: '5 km en montée 🏃‍♂️ Nouveau record personnel !',
      tag: 'Partage en direct',
      feedMock: {
        title: 'Fil',
        tabs: { all: 'Tous', following: 'Tribu', challenges: 'Défis' },
        visibility: 'Public',
        bottomNav: { home: 'Accueil', feed: 'Fil', challenges: 'Défis', profile: 'Profil' },
        categories: { motivation: 'Motivation', food: 'Repas', workout: 'Entraînement', walk: 'Marche' },
        posts: [
          { name: 'Camille', isPremium: false, category: 'food',    timeAgo: 'il y a 2 h',    caption: 'Quand tu dis "juste une assiette" mais tu sais que t\'en mangeras 4 😂', photoIdx: 1 },
          { name: 'Lucas',   isPremium: true,  category: 'workout',    timeAgo: 'il y a 39 min', caption: 'Randonnée dans la neige avec Max 🏔️',                              photoIdx: 0, videoIdx: 0 },
          { name: 'Léa',     isPremium: false, category: 'walk',    timeAgo: 'hier',          caption: 'Balade de 3 km en montagne 🏔️',                                          photoIdx: 3 },
          { name: 'Hugo',    isPremium: true,  category: 'workout', timeAgo: 'il y a 1 jour', caption: 'Lisa et moi en canoë 🛶',                                               photoIdx: 4, videoIdx: 1 },
        ]
      }
    },
    nav: {
      home: 'Accueil',
      feed: 'Fil',
      challenges: 'Défis',
      profile: 'Profil'
    }
  }
};

const ICON_MAP = {
  Scale, Users, Trophy, ChefHat, MapPin, Sparkles, CalendarRange
};

// Detect browser language
const detectLanguage = () => {
  const browserLang = navigator.language?.split('-')[0] || 'en';
  const supported = ['it', 'en', 'es', 'pt', 'de', 'fr'];
  return supported.includes(browserLang) ? browserLang : 'en';
};

const LANGUAGE_NAMES = {
  it: 'Italiano',
  en: 'English',
  es: 'Español',
  pt: 'Português',
  de: 'Deutsch',
  fr: 'Français'
};

export default function Landing() {
  const [lang, setLang] = useState(() => detectLanguage());
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [sliderPosition, setSliderPosition] = useState(50);
  // The "Share workout" mockup is a clean phone-tracker frame whose
  // single content area auto-cycles through all 5 selfies provided by
  // the product owner. No other UI clutter (no header avatar, stats,
  // hearts, captions) — by explicit user request.
  const SHARE_HERO_PHOTOS = useMemo(() => [
    '/landing/slideshow/sentiero.webp',
    '/landing/slideshow/neve.webp',
    '/landing/slideshow/miami.webp',
    '/landing/slideshow/selfie.webp',
  ], []);

  // Compressed, autoplay-friendly demo videos for the feed mockup. Files
  // live under /app/frontend/public/landing/videos/ — served as static
  // assets. ~3.4MB and ~1.8MB respectively, 720x900 (4:5), no audio,
  // +faststart enabled so playback starts before the full download.
  const SHARE_HERO_VIDEOS = useMemo(() => [
    { src: '/landing/videos/post1.mp4', poster: '/landing/videos/post1.jpg' },
    { src: '/landing/videos/post2.mp4', poster: '/landing/videos/post2.jpg' },
  ], []);
  const [shareHeroIdx, setShareHeroIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setShareHeroIdx((i) => (i + 1) % SHARE_HERO_PHOTOS.length);
    }, 3500);
    return () => clearInterval(id);
  }, [SHARE_HERO_PHOTOS.length]);
  
  const t = useMemo(() => TRANSLATIONS[lang] || TRANSLATIONS.en, [lang]);

  // Force scroll styles on mount only - simple approach without MutationObserver
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    
    // Add classes for CSS-based styling
    html.classList.add('landing-active');
    body.classList.add('landing-active');
    
    // Scroll to top
    window.scrollTo(0, 0);
    
    return () => {
      html.classList.remove('landing-active');
      body.classList.remove('landing-active');
    };
  }, []);

  const handleLanguageChange = (newLang) => {
    setLang(newLang);
    localStorage.setItem('app_language', newLang);
    // Mark as manual choice so geo-detect won't override on next start
    localStorage.setItem('app_language_manual', '1');
    document.documentElement.lang = newLang;
    // Notify the global LanguageContext so other pages (Privacy/Terms/etc.)
    // pick up the new language immediately without requiring a manual reload
    // or a second click on their own selector.
    try {
      window.dispatchEvent(
        new CustomEvent('backend-language-sync', { detail: { language: newLang } })
      );
    } catch (_) { /* no-op */ }
    setShowLangMenu(false);
  };

  // Handle slider drag
  const handleSliderChange = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const percentage = Math.min(Math.max((x / rect.width) * 100, 0), 100);
    setSliderPosition(percentage);
  };

  return (
    <div 
      className="landing-page-wrapper"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        overflowX: 'hidden',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        backgroundColor: '#080D1A'
      }}
    >
      <div className="landing-page flex flex-col bg-gradient-to-br from-[#080D1A] via-[#0a1628] to-[#0d1f3c] text-white">
      {/* Fixed Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#080D1A]/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <img src={LOGO} alt="Slimmy" className="h-8 md:h-9" />
          
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setShowLangMenu(!showLangMenu)}
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors text-sm"
              >
                <Globe className="w-4 h-4" />
                <span className="hidden sm:inline">{LANGUAGE_NAMES[lang]}</span>
                <span className="sm:hidden">{lang.toUpperCase()}</span>
              </button>
              
              {showLangMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowLangMenu(false)} />
                  <div className="absolute right-0 top-full mt-2 bg-[#0D1424] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50 min-w-[140px]">
                    {Object.entries(LANGUAGE_NAMES).map(([code, name]) => (
                      <button
                        key={code}
                        onClick={() => handleLanguageChange(code)}
                        className={`w-full px-4 py-3 text-left text-sm hover:bg-white/10 transition-colors flex items-center justify-between ${lang === code ? 'bg-white/5 text-[#E8334A]' : ''}`}
                      >
                        {name}
                        {lang === code && <Check className="w-4 h-4" />}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 lg:py-28 px-4 pt-24">
        <div className="max-w-4xl mx-auto text-center">
          {/* Animated gradient background */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#E8334A]/20 rounded-full blur-[100px] animate-pulse" />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#F59E0B]/20 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '1s' }} />
          </div>
          
          <div className="relative z-10">
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-2 leading-tight">
              {t.hero.title}
              {t.hero.titleLine2 && (
                <span className="block">{t.hero.titleLine2}</span>
              )}
              <span className="block bg-gradient-to-r from-[#E8334A] to-[#F59E0B] bg-clip-text text-transparent">
                {t.hero.subtitle}
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto mb-8 mt-6">
              {t.hero.description}
            </p>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 mt-16 max-w-lg mx-auto">
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-[#E8334A]">10K+</div>
                <div className="text-sm text-gray-400">{t.stats.users}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-[#F59E0B]">50K+</div>
                <div className="text-sm text-gray-400">{t.stats.weight}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-[#16A34A]">5K+</div>
                <div className="text-sm text-gray-400">{t.stats.challenges}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Weight Tracking Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#E8334A]/10 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#16A34A]/10 rounded-full blur-[100px]" />
        </div>
        
        <div className="max-w-5xl mx-auto relative z-10">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2">
              {t.weightTracking.title}
              <span className="block bg-gradient-to-r from-[#E8334A] to-[#16A34A] bg-clip-text text-transparent">
                {t.weightTracking.subtitle}
              </span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto mt-4">
              {t.weightTracking.description}
            </p>
          </div>
          
          {/* Chart Card */}
          <div className="max-w-3xl mx-auto">
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl border border-white/20 p-6 shadow-2xl">
              {/* Stats Row */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-3 rounded-xl bg-white/5">
                  <div className="text-xs text-gray-400 mb-1">{t.weightTracking.start}</div>
                  <div className="text-xl font-bold">85 kg</div>
                </div>
                <div className="text-center p-3 rounded-xl bg-gradient-to-r from-[#E8334A]/20 to-[#16A34A]/20">
                  <div className="text-xs text-gray-400 mb-1">{t.weightTracking.current}</div>
                  <div className="text-xl font-bold text-[#16A34A]">73.2 kg</div>
                </div>
                <div className="text-center p-3 rounded-xl bg-white/5">
                  <div className="text-xs text-gray-400 mb-1">{t.weightTracking.goal}</div>
                  <div className="text-xl font-bold">70 kg</div>
                </div>
              </div>
              
              {/* Chart */}
              <div className="relative h-64 mb-4">
                <svg viewBox="0 0 400 200" className="w-full h-full">
                  {/* Grid lines */}
                  <defs>
                    <linearGradient id="curveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#E8334A" />
                      <stop offset="100%" stopColor="#16A34A" />
                    </linearGradient>
                    <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#16A34A" stopOpacity="0.3" />
                      <stop offset="100%" stopColor="#16A34A" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  
                  {/* Horizontal grid */}
                  {[0, 1, 2, 3, 4].map(i => (
                    <line key={`grid-${i}`} x1="40" y1={30 + i * 35} x2="380" y2={30 + i * 35} stroke="white" strokeOpacity="0.1" />
                  ))}
                  
                  {/* Y-axis labels: 85kg = y30, 80kg = y65, 75kg = y100, 70kg = y135, 65kg = y170 */}
                  <text x="30" y="35" fill="#6B7280" fontSize="10" textAnchor="end">85</text>
                  <text x="30" y="70" fill="#6B7280" fontSize="10" textAnchor="end">80</text>
                  <text x="30" y="105" fill="#6B7280" fontSize="10" textAnchor="end">75</text>
                  <text x="30" y="140" fill="#6B7280" fontSize="10" textAnchor="end">70</text>
                  <text x="30" y="175" fill="#6B7280" fontSize="10" textAnchor="end">65</text>
                  
                  {/* X-axis labels */}
                  {[0, 1, 2, 3, 4, 5].map(i => (
                    <text key={`label-${i}`} x={60 + i * 60} y="195" fill="#6B7280" fontSize="10" textAnchor="middle">{i + 1}</text>
                  ))}
                  
                  {/* Ideal curve (dashed) - this is the reference line */}
                  {/* Points on ideal curve: 60,30 -> 110,50 -> 160,70 -> 210,90 -> 260,110 -> 310,130 -> 360,150 */}
                  <path 
                    d="M 60 30 L 110 50 L 160 70 L 210 90 L 260 110 L 310 130 L 360 150" 
                    fill="none" 
                    stroke="url(#curveGradient)" 
                    strokeWidth="3"
                    strokeDasharray="8 4"
                    opacity="0.7"
                  />
                  
                  {/* Actual weight line - follows the real data points */}
                  <path 
                    d="M 60 30 L 110 45 L 160 75 L 210 82 L 260 115 L 310 135 L 360 155" 
                    fill="none" 
                    stroke="#16A34A" 
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  
                  {/* Area under actual progress */}
                  <path 
                    d="M 60 30 L 110 45 L 160 75 L 210 82 L 260 115 L 310 135 L 360 155 L 360 170 L 60 170 Z" 
                    fill="url(#areaGradient)"
                  />
                  
                  {/* Goal line at 70kg = y135 */}
                  <line x1="40" y1="135" x2="380" y2="135" stroke="#F59E0B" strokeWidth="1" strokeDasharray="4 4" opacity="0.5" />
                  <text x="385" y="138" fill="#F59E0B" fontSize="9">Goal</text>
                  
                  {/* Data points - red if ABOVE ideal curve (bad), green if BELOW ideal curve (good) */}
                  {/* In SVG: smaller y = higher on screen = higher weight = bad */}
                  {/* Ideal curve y values: x60=30, x110=50, x160=70, x210=90, x260=110, x310=130, x360=150 */}
                  {[
                    { x: 60, y: 30, idealY: 30 },   // Start: on curve = GREEN
                    { x: 110, y: 45, idealY: 50 },  // y45 < idealY50 = ABOVE curve = RED (bad day)
                    { x: 160, y: 75, idealY: 70 },  // y75 > idealY70 = BELOW curve = GREEN
                    { x: 210, y: 82, idealY: 90 },  // y82 < idealY90 = ABOVE curve = RED (bad day)
                    { x: 260, y: 115, idealY: 110 }, // y115 > idealY110 = BELOW curve = GREEN
                    { x: 310, y: 135, idealY: 130 }, // y135 > idealY130 = BELOW curve = GREEN
                    { x: 360, y: 155, idealY: 150 }, // y155 > idealY150 = BELOW curve = GREEN - Current
                  ].map((point, i) => {
                    // isGood = point is ON or BELOW the ideal curve (y >= idealY means lower or equal weight)
                    const isGood = point.y >= point.idealY;
                    const isLast = i === 6;
                    return (
                      <g key={`point-${i}`}>
                        <circle 
                          cx={point.x} 
                          cy={point.y} 
                          r={isLast ? 6 : 4} 
                          fill={isGood ? "#16A34A" : "#E8334A"}
                          stroke="white"
                          strokeWidth="2"
                        />
                        {isLast && (
                          <circle cx={point.x} cy={point.y} r="10" fill="#16A34A" fillOpacity="0.3">
                            <animate attributeName="r" values="10;14;10" dur="2s" repeatCount="indefinite" />
                            <animate attributeName="fill-opacity" values="0.3;0.1;0.3" dur="2s" repeatCount="indefinite" />
                          </circle>
                        )}
                      </g>
                    );
                  })}
                </svg>
                
                {/* X-axis label */}
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-xs text-gray-500">
                  {t.weightTracking.monthsLabel}
                </div>
              </div>
              
              {/* Bottom Stats */}
              <div className="flex justify-center gap-8 pt-4 border-t border-white/10">
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#16A34A]">-11.8 kg</div>
                  <div className="text-xs text-gray-400">{t.weightTracking.lost}</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#F59E0B]">-3.2 kg</div>
                  <div className="text-xs text-gray-400">{t.weightTracking.toGo}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Transformation Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        {/* Background glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#8B5CF6]/10 rounded-full blur-[120px]" />
        </div>
        
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2">
              {t.aiTransform.title}
              <span className="block bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] bg-clip-text text-transparent">
                {t.aiTransform.subtitle}
              </span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto mt-4">
              {t.aiTransform.description}
            </p>
            
            {/* Stat badge */}
            <div className="inline-flex items-center gap-2 mt-6 px-4 py-2 rounded-full bg-gradient-to-r from-[#8B5CF6]/20 to-[#EC4899]/20 border border-[#8B5CF6]/30">
              <span className="text-2xl font-bold bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] bg-clip-text text-transparent">
                {t.aiTransform.stat}
              </span>
              <span className="text-sm text-gray-300">{t.aiTransform.statText}</span>
            </div>
          </div>
          
          {/* Before/After Slider */}
          <div className="max-w-lg mx-auto">
            <div 
              className="relative rounded-3xl overflow-hidden shadow-2xl cursor-ew-resize select-none"
              style={{ aspectRatio: '1/1' }}
              onMouseMove={(e) => e.buttons === 1 && handleSliderChange(e)}
              onMouseDown={handleSliderChange}
              onTouchMove={handleSliderChange}
              onTouchStart={handleSliderChange}
            >
              {/* After image (bottom layer) */}
              <img 
                src="https://customer-assets.emergentagent.com/job_57e2e740-274d-4480-9155-167f0e5dcf96/artifacts/8u29ar78_after.webp"
                alt="After transformation"
                className="absolute inset-0 w-full h-full object-cover"
                draggable="false"
              />
              
              {/* Before image (top layer, clipped) */}
              <div 
                className="absolute inset-0 overflow-hidden"
                style={{ width: `${sliderPosition}%` }}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_57e2e740-274d-4480-9155-167f0e5dcf96/artifacts/fbsbkfve_before.webp"
                  alt="Before transformation"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ width: `${100 / (sliderPosition / 100)}%`, maxWidth: 'none' }}
                  draggable="false"
                />
              </div>
              
              {/* Slider line and handle */}
              <div 
                className="absolute top-0 bottom-0 w-1 bg-white shadow-lg"
                style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
              >
                {/* Handle */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center">
                  <div className="flex gap-1">
                    <ChevronRight className="w-4 h-4 text-gray-600 rotate-180" />
                    <ChevronRight className="w-4 h-4 text-gray-600" />
                  </div>
                </div>
              </div>
              
              {/* Labels */}
              <div className="absolute bottom-4 left-4 px-3 py-1 rounded-full bg-black/50 backdrop-blur text-sm font-medium">
                {t.aiTransform.before}
              </div>
              <div className="absolute bottom-4 right-4 px-3 py-1 rounded-full bg-black/50 backdrop-blur text-sm font-medium">
                {t.aiTransform.after}
              </div>
            </div>
            
            {/* Drag hint */}
            <p className="text-center text-gray-500 text-sm mt-4">
              {t.aiTransform.dragHint}
            </p>
          </div>
        </div>
      </section>

      {/* Social/Community Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#E8334A]/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#F59E0B]/10 rounded-full blur-[100px]" />
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2">
              {t.social.title}
              <span className="block bg-gradient-to-r from-[#E8334A] to-[#F59E0B] bg-clip-text text-transparent">
                {t.social.subtitle}
              </span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto mt-4">
              {t.social.description}
            </p>
          </div>
          
          {/* Phone mockup with realistic auto-scrolling Feed page.
              Replaces the previous 2x3 photo grid: shows what the actual
              app looks like — a TikTok-style social feed where users
              share their journey moments. Posts loop seamlessly via
              `feed-scroll-loop` keyframes (28s, calm pace). */}
          <div className="flex justify-center">
            <div
              className="relative w-full max-w-[330px] bg-gradient-to-b from-[#3a3a3a] via-[#2a2a2a] to-[#1f1f1f] rounded-[2.8rem] p-3 shadow-2xl shadow-black/60 ring-1 ring-white/10"
              data-testid="landing-feed-mockup"
            >
              {/* Side buttons for realism */}
              <span className="absolute left-[-2px] top-[100px] w-[3px] h-7  rounded-l-sm bg-[#2a2a2a]" />
              <span className="absolute left-[-2px] top-[140px] w-[3px] h-10 rounded-l-sm bg-[#2a2a2a]" />
              <span className="absolute left-[-2px] top-[180px] w-[3px] h-10 rounded-l-sm bg-[#2a2a2a]" />
              <span className="absolute right-[-2px] top-[130px] w-[3px] h-14 rounded-r-sm bg-[#2a2a2a]" />

              {/* Phone screen — white app surface. Shorter aspect on mobile
                  to keep the mockup compact, taller on sm+ for desktop. */}
              <div className="relative bg-white rounded-[2.2rem] overflow-hidden flex flex-col aspect-[9/15] sm:aspect-[9/18.5]">
                {/* Status bar */}
                <div className="flex items-center justify-between px-5 pt-2 pb-1 text-black text-[10px] font-semibold flex-shrink-0 relative">
                  <span>9:41</span>
                  <span className="absolute left-1/2 -translate-x-1/2 w-24 h-[22px] bg-black rounded-full" />
                  <span className="inline-block w-5 h-2.5 rounded-[3px] border border-black/80 relative">
                    <span className="absolute inset-0.5 bg-black rounded-[1px]" />
                  </span>
                </div>

                {/* Feed header */}
                <div className="px-3 py-2 flex items-center justify-between flex-shrink-0">
                  <h3 className="text-gray-900 text-lg font-bold tracking-tight">{t.shareWorkout.feedMock.title}</h3>
                  <div className="flex items-center gap-1.5">
                    <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
                      <Search className="w-3 h-3 text-gray-600" />
                    </div>
                    <div className="w-6 h-6 rounded-full bg-[#FFE4E6] flex items-center justify-center">
                      <RefreshCw className="w-3 h-3 text-[#E8334A]" />
                    </div>
                  </div>
                </div>

                {/* Tabs */}
                <div className="px-2 pb-1.5 flex gap-1 flex-shrink-0">
                  {[
                    { key: 'all', icon: Globe, active: true },
                    { key: 'following', icon: UserCheck, active: false },
                    { key: 'challenges', icon: Trophy, active: false },
                  ].map(({ key, icon: Icon, active }) => (
                    <div
                      key={key}
                      className={`flex-1 py-1.5 rounded-md flex items-center justify-center gap-1 ${
                        active ? 'bg-[#E8334A] text-white' : 'bg-transparent text-gray-500'
                      }`}
                    >
                      <Icon className="w-3 h-3" />
                      <span className="text-[11px] font-semibold">{t.shareWorkout.feedMock.tabs[key]}</span>
                    </div>
                  ))}
                </div>

                {/* Scrolling posts area */}
                <div className="flex-1 bg-gray-50 overflow-hidden relative">
                  <div className="feed-scroll-loop">
                    {/* Render posts twice so translateY(-50%) loops seamlessly */}
                    {[...t.shareWorkout.feedMock.posts, ...t.shareWorkout.feedMock.posts].map((post, i) => {
                      const avatarGradients = [
                        'from-blue-500 to-indigo-600',
                        'from-pink-500 to-rose-600',
                        'from-emerald-500 to-teal-600',
                        'from-cyan-500 to-blue-600',
                        'from-purple-500 to-fuchsia-600',
                      ];
                      const categoryStyles = {
                        motivation: { bg: 'bg-purple-100', text: 'text-purple-700', Icon: Heart },
                        food:       { bg: 'bg-orange-100', text: 'text-orange-700', Icon: ChefHat },
                        workout:    { bg: 'bg-green-100',  text: 'text-green-700',  Icon: Flame },
                        walk:       { bg: 'bg-blue-100',   text: 'text-blue-700',   Icon: Navigation },
                      };
                      const cat = categoryStyles[post.category] || categoryStyles.motivation;
                      const grad = avatarGradients[post.photoIdx % avatarGradients.length];
                      // Realistic reaction counts per post (varies by photoIdx for visual richness)
                      const reactionSets = [
                        { flex: 24, fire: 18, heart: 142, comments: 12 },
                        { flex: 8,  fire: 31, heart: 47,  comments: 23 },
                        { flex: 5,  fire: 58, heart: 89,  comments: 34 },
                        { flex: 67, fire: 22, heart: 95,  comments: 7  },
                        { flex: 41, fire: 19, heart: 108, comments: 15 },
                      ];
                      const r = reactionSets[post.photoIdx % reactionSets.length];
                      return (
                        <div key={i} className="bg-white mx-2 my-2 rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                          {/* Post header */}
                          <div className="flex items-start gap-2 p-2.5">
                            <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${grad} flex items-center justify-center flex-shrink-0`}>
                              <span className="text-white text-xs font-bold">{post.name[0]}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1 mb-1">
                                {post.isPremium && <Crown className="w-3 h-3 text-[#F59E0B] flex-shrink-0" />}
                                <span className="text-xs font-bold text-gray-900 truncate">{post.name}</span>
                              </div>
                              <div className="flex items-center gap-1 flex-wrap">
                                <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full ${cat.bg} ${cat.text} text-[9px] font-semibold`}>
                                  <cat.Icon className="w-2.5 h-2.5" />
                                  {t.shareWorkout.feedMock.categories[post.category]}
                                </span>
                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[9px] font-semibold">
                                  <Globe className="w-2.5 h-2.5" />
                                  {t.shareWorkout.feedMock.visibility}
                                </span>
                              </div>
                              <p className="text-[9px] text-gray-400 mt-1">{post.timeAgo}</p>
                            </div>
                            <MoreHorizontal className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          </div>

                          {/* Caption */}
                          {post.caption && (
                            <p className="px-2.5 pb-2 text-[11px] text-gray-800 leading-snug whitespace-pre-line">{post.caption}</p>
                          )}

                          {/* Image or Video — vertical 4:5 Instagram-style */}
                          <div className="px-2 pb-2 relative">
                            {post.videoIdx !== undefined ? (
                              <>
                                <video
                                  src={SHARE_HERO_VIDEOS[post.videoIdx].src}
                                  poster={SHARE_HERO_VIDEOS[post.videoIdx].poster}
                                  autoPlay
                                  muted
                                  loop
                                  playsInline
                                  preload="auto"
                                  className="w-full aspect-[4/5] object-cover rounded-2xl bg-black"
                                />
                                {/* Subtle "video" indicator — top-right of media */}
                                <span className="absolute top-3 right-4 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur-sm text-white text-[10px] font-semibold">
                                  <Video className="w-3 h-3" />
                                  Video
                                </span>
                              </>
                            ) : (
                              <img
                                src={SHARE_HERO_PHOTOS[post.photoIdx]}
                                alt=""
                                loading="lazy"
                                className="w-full aspect-[4/5] object-cover rounded-2xl"
                              />
                            )}
                          </div>

                          {/* Reactions row */}
                          <div className="flex items-center justify-between px-2.5 pb-2.5">
                            <div className="flex items-center gap-1">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-[11px] font-semibold text-gray-700">
                                <span>💪</span><span>{r.flex}</span>
                              </span>
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-[11px] font-semibold text-gray-700">
                                <span>🔥</span><span>{r.fire}</span>
                              </span>
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFE4E6] text-[11px] font-semibold text-[#E8334A]">
                                <span>❤️</span><span>{r.heart}</span>
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100">
                                <MessageCircle className="w-3 h-3 text-gray-500" />
                                <span className="text-[11px] font-semibold text-gray-700">{r.comments}</span>
                              </div>
                              <div className="px-2 py-1 rounded-full bg-gray-100 flex items-center justify-center">
                                <Share2 className="w-3 h-3 text-gray-500" />
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Bottom nav */}
                <div className="bg-white border-t border-gray-100 px-2 py-2 flex items-center justify-around flex-shrink-0 relative">
                  {[
                    { Icon: Home,           label: t.shareWorkout.feedMock.bottomNav.home,       active: false },
                    { Icon: MessageCircle,  label: t.shareWorkout.feedMock.bottomNav.feed,       active: true  },
                    { isPlus: true },
                    { Icon: Trophy,         label: t.shareWorkout.feedMock.bottomNav.challenges, active: false },
                    { Icon: User,           label: t.shareWorkout.feedMock.bottomNav.profile,    active: false },
                  ].map((it, idx) => (
                    it.isPlus ? (
                      <div key={idx} className="w-10 h-10 rounded-full bg-[#E8334A] flex items-center justify-center -mt-3 shadow-lg shadow-[#E8334A]/30">
                        <Plus className="w-5 h-5 text-white" />
                      </div>
                    ) : (
                      <div key={idx} className="flex flex-col items-center gap-0.5 flex-1">
                        <it.Icon className={`w-4 h-4 ${it.active ? 'text-[#E8334A]' : 'text-gray-400'}`} />
                        <span className={`text-[10px] font-semibold ${it.active ? 'text-[#E8334A]' : 'text-gray-400'}`}>{it.label}</span>
                      </div>
                    )
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-0 w-[400px] h-[400px] bg-[#F59E0B]/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#16A34A]/10 rounded-full blur-[100px]" />
        </div>
        
        <div className="max-w-5xl mx-auto relative z-10">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2">
              {t.challenges.title}
              <span className="block bg-gradient-to-r from-[#F59E0B] to-[#16A34A] bg-clip-text text-transparent">
                {t.challenges.subtitle}
              </span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto mt-4">
              {t.challenges.description}
            </p>
          </div>
          
          {/* Challenge Card */}
          <div className="max-w-2xl mx-auto">
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl border border-white/20 overflow-hidden shadow-2xl">
              {/* Card Header */}
              <div className="p-6 border-b border-white/10 bg-gradient-to-r from-[#F59E0B]/20 to-[#16A34A]/20">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold">{t.challenges.challengeTitle}</h3>
                    <p className="text-gray-400 text-sm mt-1">{t.challenges.goal}</p>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                    <Trophy className="w-5 h-5 text-[#F59E0B]" />
                    <span className="font-medium text-sm whitespace-nowrap">8 {t.challenges.participants}</span>
                  </div>
                </div>
                
                {/* Stats */}
                <div className="flex gap-6 mt-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-[#F59E0B]">18</div>
                    <div className="text-xs text-gray-400">{t.challenges.daysLeft}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-[#16A34A]">23.5</div>
                    <div className="text-xs text-gray-400">{t.challenges.totalLost}</div>
                  </div>
                </div>
              </div>
              
              {/* Participants List */}
              <div className="p-6 space-y-4">
                {[
                  { progress: 92, lost: 4.2 },
                  { progress: 85, lost: 3.8 },
                  { progress: 78, lost: 3.5 },
                  { progress: 72, lost: 3.2 },
                  { progress: 65, lost: 2.9 },
                  { progress: 58, lost: 2.6 },
                  { progress: 45, lost: 2.0 },
                  { progress: 32, lost: 1.3 }
                ].map((participant, i) => (
                  <div key={`participant-${participant.progress}`} className="flex items-center gap-4">
                    {/* Avatar */}
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                      style={{ 
                        background: `linear-gradient(135deg, ${['#E8334A', '#F59E0B', '#3B82F6', '#16A34A', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'][i]} 0%, ${['#F59E0B', '#16A34A', '#8B5CF6', '#14B8A6', '#EC4899', '#E8334A', '#3B82F6', '#F59E0B'][i]} 100%)`
                      }}
                    >
                      {t.challenges.names[i]?.[0] || 'U'}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm">{t.challenges.names[i]}</span>
                        <span className="text-xs text-[#16A34A] font-medium">-{participant.lost}kg</span>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all duration-500"
                          style={{ 
                            width: `${participant.progress}%`,
                            background: `linear-gradient(90deg, #F59E0B 0%, #16A34A 100%)`
                          }}
                        />
                      </div>
                    </div>
                    
                    {/* Percentage */}
                    <div className="text-sm font-bold text-gray-400 w-12 text-right">
                      {participant.progress}%
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Meal Plan Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-[#10B981]/10 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-[#F59E0B]/8 rounded-full blur-[100px]" />
        </div>
        
        <div className="max-w-5xl mx-auto relative z-10">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2">
              {t.mealPlan.title}
              <span className="block bg-gradient-to-r from-[#10B981] to-[#F59E0B] bg-clip-text text-transparent">
                {t.mealPlan.subtitle}
              </span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto mt-4">
              {t.mealPlan.description}
            </p>
          </div>
          
          {/* Content: Phone mockup + Features */}
          <div className="grid md:grid-cols-2 gap-8 items-center max-w-4xl mx-auto">
            {/* Phone Mockup - Light theme matching real app screenshot */}
            <div className="max-w-[300px] mx-auto w-full">
              <div className="relative bg-gradient-to-b from-[#2a2a3e] to-[#1a1a2e] rounded-[2.8rem] p-3 shadow-2xl border border-white/10">
                <div className="relative rounded-[2.2rem] overflow-hidden bg-[#F7F7F7]" style={{ aspectRatio: '9/18.5' }}>
                  {/* Status Bar */}
                  <div className="absolute top-0 left-0 right-0 z-20 px-5 py-1.5 flex items-center justify-between text-[10px] text-gray-800 bg-[#F7F7F7]">
                    <span className="font-semibold">9:41</span>
                    <div className="absolute left-1/2 -translate-x-1/2 w-24 h-[22px] bg-black rounded-full" />
                    <div className="flex items-center gap-0.5">
                      <div className="w-4 h-2.5 border border-gray-800 rounded-[2px] relative">
                        <div className="absolute inset-[1.5px] bg-gray-800 rounded-[1px]" style={{width:'60%'}} />
                      </div>
                    </div>
                  </div>
                  
                  {/* App Header */}
                  <div className="pt-9 px-3.5 pb-2 bg-[#F7F7F7]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ArrowLeft className="w-4 h-4 text-gray-800" />
                        <div>
                          <p className="text-gray-900 text-[12px] font-bold">{t.mealPlan.header}</p>
                          <div className="flex items-center gap-1">
                            <Crown className="w-2.5 h-2.5 text-amber-500" />
                            <p className="text-gray-400 text-[8px]">{t.mealPlan.headerSub}</p>
                          </div>
                        </div>
                      </div>
                      <Pencil className="w-3.5 h-3.5 text-gray-400" />
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="px-3 pb-12">
                    {/* Daily Average Card */}
                    <div className="bg-white rounded-2xl p-3 mb-2.5 shadow-sm border border-gray-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-400 text-[9px] font-medium">{t.mealPlan.dailyAvg}</p>
                          <div className="flex items-baseline gap-1 mt-0.5">
                            <span className="text-orange-500 text-[10px]">🔥</span>
                            <span className="text-gray-900 text-[18px] font-bold">{t.mealPlan.avgCal}</span>
                            <span className="text-gray-400 text-[9px]">{t.mealPlan.calUnit}</span>
                          </div>
                        </div>
                        <span className="px-2 py-0.5 rounded-full border border-emerald-500 text-emerald-600 text-[8px] font-semibold">{t.mealPlan.balanced}</span>
                      </div>
                    </div>
                    
                    {/* Day Card - Expanded */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                      {/* Day Header */}
                      <div className="flex items-center justify-between p-3 pb-2">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-lg bg-emerald-500 flex items-center justify-center text-[10px] font-bold text-white">
                            {t.mealPlan.sample.day[0]}
                          </div>
                          <div>
                            <p className="text-gray-900 text-[11px] font-semibold">{t.mealPlan.sample.day}</p>
                            <p className="text-gray-400 text-[8px]">{t.mealPlan.sample.totalCal}</p>
                          </div>
                        </div>
                        <ChevronUp className="w-3.5 h-3.5 text-gray-400" />
                      </div>
                      
                      {/* Meals */}
                      <div className="px-3 pb-3 space-y-2 border-t border-gray-50">
                        {t.mealPlan.sample.meals.map((meal, i) => (
                          <div key={`landing-meal-${i}`} className="bg-gray-50 rounded-xl p-2.5 mt-2">
                            <div className="flex items-start gap-2">
                              <span className="text-[14px] mt-0.5">{meal.emoji}</span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <p className="text-gray-400 text-[7px] font-semibold uppercase tracking-wide">{meal.type}</p>
                                  <span className="text-emerald-600 text-[8px] font-bold">{meal.cal} kcal</span>
                                </div>
                                <p className="text-gray-900 text-[10px] font-semibold mt-0.5">{meal.name}</p>
                                <p className="text-gray-400 text-[7.5px] mt-1 leading-relaxed">{meal.ingredients}</p>
                                
                                {/* Preparation */}
                                <div className="mt-1.5 pt-1.5 border-t border-gray-200">
                                  <div className="flex items-center gap-0.5 mb-0.5">
                                    <span className="text-[6px]">🍳</span>
                                    <span className="text-emerald-600 text-[6px] font-bold">{t.mealPlan.prepLabel}</span>
                                  </div>
                                  <p className="text-gray-500 text-[7px] leading-relaxed">{meal.preparation}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Bottom Nav */}
                  <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-2 py-1.5 grid grid-cols-5 items-center">
                    {[
                      { icon: Home, label: t.nav?.home || 'Home', active: true },
                      { icon: MessageCircle, label: t.nav?.feed || 'Feed', active: false },
                      { isFab: true },
                      { icon: Trophy, label: t.nav?.challenges || 'Sfide', active: false },
                      { icon: User, label: t.nav?.profile || 'Profilo', active: false }
                    ].map((item, i) => (
                      <div key={`nav-mock-${i}`} className="flex flex-col items-center justify-center gap-0.5">
                        {item.isFab ? (
                          <div className="w-8 h-8 rounded-full bg-[#E8334A] flex items-center justify-center -mt-3 shadow-lg">
                            {/* Speech bubble with + */}
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                              <line x1="12" y1="7" x2="12" y2="13" />
                              <line x1="9" y1="10" x2="15" y2="10" />
                            </svg>
                          </div>
                        ) : (
                          <>
                            <item.icon className={`w-3.5 h-3.5 ${item.active ? 'text-gray-700' : 'text-gray-400'}`} />
                            <span className={`text-[5.5px] font-medium uppercase text-center ${item.active ? 'text-gray-700' : 'text-gray-400'}`}>{item.label}</span>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Features list */}
            <div className="space-y-4">
              {t.mealPlan.features.map((feature, i) => (
                <div key={`mp-feat-${i}`} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-xl bg-[#10B981]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-[#10B981]" />
                  </div>
                  <p className="text-gray-300 text-base">{feature}</p>
                </div>
              ))}
              
            </div>
          </div>
        </div>
      </section>

      {/* Shopping List Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#F59E0B]/10 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#10B981]/8 rounded-full blur-[100px]" />
        </div>
        
        <div className="max-w-5xl mx-auto relative z-10">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2">
              {t.shoppingList.title}
              <span className="block bg-gradient-to-r from-[#F59E0B] to-[#10B981] bg-clip-text text-transparent">
                {t.shoppingList.subtitle}
              </span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto mt-4">
              {t.shoppingList.description}
            </p>
          </div>
          
          {/* Content: Phone mockup + Description */}
          <div className="grid md:grid-cols-2 gap-8 items-center max-w-4xl mx-auto">
            {/* Phone Mockup - Light theme matching real app screenshot */}
            <div className="max-w-[300px] mx-auto w-full order-2 md:order-1">
              <div className="relative bg-gradient-to-b from-[#2a2a3e] to-[#1a1a2e] rounded-[2.8rem] p-3 shadow-2xl border border-white/10">
                <div className="relative rounded-[2.2rem] overflow-hidden bg-[#F7F7F7]" style={{ aspectRatio: '9/18.5' }}>
                  {/* Status Bar */}
                  <div className="absolute top-0 left-0 right-0 z-20 px-5 py-1.5 flex items-center justify-between text-[10px] text-gray-800 bg-[#F7F7F7]">
                    <span className="font-semibold">9:53</span>
                    <div className="absolute left-1/2 -translate-x-1/2 w-24 h-[22px] bg-black rounded-full" />
                    <div className="flex items-center gap-0.5">
                      <div className="w-4 h-2.5 border border-gray-800 rounded-[2px] relative">
                        <div className="absolute inset-[1.5px] bg-gray-800 rounded-[1px]" style={{width:'73%'}} />
                      </div>
                    </div>
                  </div>
                  
                  {/* App Header */}
                  <div className="pt-9 px-3.5 pb-2 bg-[#F7F7F7]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ArrowLeft className="w-4 h-4 text-gray-800" />
                        <div>
                          <p className="text-gray-900 text-[12px] font-bold">{t.shoppingList.title.replace(' AI', '').replace(' IA', '')}</p>
                          <div className="flex items-center gap-1">
                            <ShoppingCart className="w-2.5 h-2.5 text-amber-500" />
                            <p className="text-gray-400 text-[8px]">{t.shoppingList.subtitle}</p>
                          </div>
                        </div>
                      </div>
                      <Share2 className="w-3.5 h-3.5 text-gray-400" />
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="px-3 pb-12 overflow-y-auto" style={{ maxHeight: 'calc(100% - 80px)' }}>
                    {/* Day Selector Label */}
                    <p className="text-gray-500 text-[8px] font-semibold uppercase tracking-wide mb-1.5 px-0.5">{t.shoppingList.forHowManyDays}</p>
                    
                    {/* Day Selector Buttons - "1 giorno" selected */}
                    <div className="flex gap-1.5 mb-3">
                      {t.shoppingList.dayOptions.map((day, i) => (
                        <button
                          key={`day-opt-${i}`}
                          className={`flex-1 py-1.5 rounded-lg text-[8px] font-semibold transition-all ${
                            i === 0
                              ? 'bg-[#10B981] text-white shadow-sm'
                              : 'bg-white border border-gray-200 text-gray-700'
                          }`}
                        >
                          {day}
                        </button>
                      ))}
                    </div>
                    
                    {/* Single Day Card (Lunedì) + Progress */}
                    <div className="bg-white rounded-xl p-2.5 mb-3 shadow-sm border border-gray-100">
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-gray-700 text-[10px] font-medium">{t.shoppingList.dayLabel || 'Lunedì'}</p>
                        <span className="text-[#10B981] text-[10px] font-bold">4/10</span>
                      </div>
                      {/* Progress bar */}
                      <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full w-[40%] bg-[#10B981] rounded-full" />
                      </div>
                    </div>
                    
                    {/* Category 1: Frutta e Verdura */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-2.5">
                      <div className="flex items-center gap-2 p-2.5 pb-1.5">
                        <span className="text-[14px]">🥬</span>
                        <span className="text-gray-900 text-[10px] font-bold">{t.shoppingList.categories.produce}</span>
                        <span className="text-gray-400 text-[8px]">(5)</span>
                      </div>
                      <div className="px-2.5 pb-2.5 space-y-1.5">
                        {t.shoppingList.sampleItems.map((item, i) => (
                          <div key={`shop-item-${i}`} className="flex items-center gap-2">
                            <div className={`w-3.5 h-3.5 rounded border-[1.5px] flex-shrink-0 flex items-center justify-center ${i < 4 ? 'bg-[#10B981] border-[#10B981]' : 'border-gray-300'}`}>
                              {i < 4 && (
                                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                            <span className={`text-[9px] ${i < 4 ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Category 2: Carne e Pesce */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-2.5">
                      <div className="flex items-center gap-2 p-2.5 pb-1.5">
                        <span className="text-[14px]">🥩</span>
                        <span className="text-gray-900 text-[10px] font-bold">{t.shoppingList.categories.proteins}</span>
                        <span className="text-gray-400 text-[8px]">(2)</span>
                      </div>
                      <div className="px-2.5 pb-2.5 space-y-1.5">
                        {(t.shoppingList.proteinItems || ['150g chicken', '150g salmon']).map((item, i) => (
                          <div key={`prot-item-${i}`} className="flex items-center gap-2">
                            <div className="w-3.5 h-3.5 rounded border-[1.5px] border-gray-300 flex-shrink-0" />
                            <span className="text-gray-700 text-[9px]">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Category 3: Latticini e Uova (partial) */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                      <div className="flex items-center gap-2 p-2.5 pb-1.5">
                        <span className="text-[14px]">🥛</span>
                        <span className="text-gray-900 text-[10px] font-bold">{t.shoppingList.categories.dairy}</span>
                        <span className="text-gray-400 text-[8px]">(2)</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Bottom Nav */}
                  <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-2 py-1.5 grid grid-cols-5 items-center">
                    {[
                      { icon: Home, label: t.nav?.home || 'Home', active: true },
                      { icon: MessageCircle, label: t.nav?.feed || 'Feed', active: false },
                      { isFab: true },
                      { icon: Trophy, label: t.nav?.challenges || 'Sfide', active: false },
                      { icon: User, label: t.nav?.profile || 'Profilo', active: false }
                    ].map((item, i) => (
                      <div key={`nav-shop-${i}`} className="flex flex-col items-center justify-center gap-0.5">
                        {item.isFab ? (
                          <div className="w-8 h-8 rounded-full bg-[#E8334A] flex items-center justify-center -mt-3 shadow-lg">
                            {/* Speech bubble with + */}
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                              <line x1="12" y1="7" x2="12" y2="13" />
                              <line x1="9" y1="10" x2="15" y2="10" />
                            </svg>
                          </div>
                        ) : (
                          <>
                            <item.icon className={`w-3.5 h-3.5 ${item.active ? 'text-gray-700' : 'text-gray-400'}`} />
                            <span className={`text-[5.5px] font-medium uppercase text-center ${item.active ? 'text-gray-700' : 'text-gray-400'}`}>{item.label}</span>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Features list */}
            <div className="space-y-4 order-1 md:order-2">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-xl bg-[#F59E0B]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CalendarRange className="w-4 h-4 text-[#F59E0B]" />
                </div>
                <div>
                  <p className="text-white font-semibold">{t.shoppingList.dayOptions[0]}, {t.shoppingList.dayOptions[1]}, {t.shoppingList.dayOptions[2]}, {t.shoppingList.dayOptions[3]}</p>
                  <p className="text-gray-400 text-sm">{t.shoppingList.forHowManyDays}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-xl bg-[#10B981]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-4 h-4 text-[#10B981]" />
                </div>
                <div>
                  <p className="text-white font-semibold">{t.shoppingList.categories.produce}</p>
                  <p className="text-gray-400 text-sm">{t.shoppingList.categories.proteins} · {t.shoppingList.categories.dairy} · {t.shoppingList.categories.grains}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-xl bg-[#8B5CF6]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <ShoppingCart className="w-4 h-4 text-[#8B5CF6]" />
                </div>
                <div>
                  <p className="text-white font-semibold">{t.shoppingList.progress}</p>
                  <p className="text-gray-400 text-sm">{t.shoppingList.weekDays.split(',').slice(0, 3).join(', ')}...</p>
                </div>
              </div>
              
            </div>
          </div>
        </div>
      </section>

      {/* Chef AI Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/3 w-[500px] h-[500px] bg-[#16A34A]/10 rounded-full blur-[120px]" />
        </div>
        
        <div className="max-w-5xl mx-auto relative z-10">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2">
              {t.chefAi.title}
              <span className="block bg-gradient-to-r from-[#16A34A] to-[#22D3EE] bg-clip-text text-transparent">
                {t.chefAi.subtitle}
              </span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto mt-4">
              {t.chefAi.description}
            </p>
          </div>
          
          {/* Recipe Card */}
          <div className="max-w-3xl mx-auto">
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl border border-white/20 overflow-hidden shadow-2xl">
              <div className="grid md:grid-cols-2">
                {/* Image */}
                <div className="relative h-64 md:h-auto">
                  <img 
                    src="https://images.unsplash.com/photo-1532550907401-a500c9a57435?w=800"
                    alt={t.chefAi.recipe.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 rounded-full bg-[#16A34A] text-white text-xs font-medium flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      {t.chefAi.badge}
                    </span>
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">{t.chefAi.recipe.name}</h3>
                  
                  {/* Stats */}
                  <div className="flex gap-4 mb-4">
                    <div className="flex items-center gap-1 text-sm text-gray-400">
                      <span className="text-[#F59E0B]">⏱</span> {t.chefAi.recipe.time}
                    </div>
                    <div className="flex items-center gap-1 text-sm text-gray-400">
                      <span className="text-[#E8334A]">🔥</span> {t.chefAi.recipe.calories}
                    </div>
                    <div className="flex items-center gap-1 text-sm text-gray-400">
                      <span className="text-[#16A34A]">💪</span> {t.chefAi.recipe.protein}
                    </div>
                  </div>
                  
                  {/* Ingredients */}
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-2">
                      {t.chefAi.recipe.ingredients.map((ing, i) => (
                        <span key={`ing-${ing}`} className="px-3 py-1 rounded-full bg-white/10 text-sm">
                          {ing}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-3 mt-6">
                    <button className="px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 transition-colors">
                      <Users className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <p className="text-center text-gray-500 text-xs mt-3">
                    {t.chefAi.recipe.shareText}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GPS Tracker Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#3B82F6]/10 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#16A34A]/10 rounded-full blur-[100px]" />
        </div>
        
        <div className="max-w-5xl mx-auto relative z-10">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2">
              {t.gpsTracker.title}
              <span className="block bg-gradient-to-r from-[#3B82F6] to-[#16A34A] bg-clip-text text-transparent">
                {t.gpsTracker.subtitle}
              </span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto mt-4">
              {t.gpsTracker.description}
            </p>
          </div>
          
          {/* Phone Mockup - Realistic Camminata screen */}
          <div className="max-w-[300px] mx-auto">
            {/* Phone Frame */}
            <div className="relative bg-gradient-to-b from-[#2a2a3e] to-[#1a1a2e] rounded-[2.8rem] p-3 shadow-2xl border border-white/10">
              {/* Screen */}
              <div className="relative rounded-[2.2rem] overflow-hidden bg-[#080D1A]" style={{ aspectRatio: '9/18' }}>
                {/* Status Bar */}
                <div className="absolute top-0 left-0 right-0 z-20 px-5 py-1.5 flex items-center justify-between text-[10px] text-white bg-[#080D1A]">
                  <span className="font-semibold">9:41</span>
                  <div className="absolute left-1/2 -translate-x-1/2 w-24 h-[22px] bg-black rounded-full" />
                  <div className="flex items-center gap-0.5">
                    <div className="w-4 h-2.5 border border-white rounded-[2px] relative">
                      <div className="absolute inset-[1.5px] bg-white rounded-[1px]" style={{width:'73%'}} />
                    </div>
                  </div>
                </div>

                {/* App Header */}
                <div className="pt-9 px-4 pb-3 bg-[#080D1A] border-b border-white/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center">
                        <ChevronLeft className="w-4 h-4 text-white" />
                      </div>
                      <h1 className="text-white text-[14px] font-semibold">{t.gpsTracker.walkTitle || 'Camminata'}</h1>
                    </div>
                    {/* GPS Satellite Badge */}
                    <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-green-500/20 text-green-400 text-[9px] font-medium">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                      </svg>
                      <span>5</span>
                    </div>
                  </div>
                </div>

                {/* Map Area */}
                <div className="mx-3 mt-2 rounded-2xl overflow-hidden border border-white/10" style={{ height: '120px' }}>
                  <div className="w-full h-full relative">
                    <img
                      src="https://customer-assets.emergentagent.com/job_abc8b3a8-9991-4796-b26e-2900581cbdb1/artifacts/f7ijpokk_mappa.png"
                      alt="GPS walking route map"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute" style={{ top: '52%', left: '12%', transform: 'translate(-50%, -50%)' }}>
                      <div className="absolute inset-0 w-6 h-6 -m-0.5 rounded-full bg-[#16A34A]/40 animate-ping" />
                      <div className="relative w-5 h-5 rounded-full bg-[#16A34A] border-2 border-white shadow-lg flex items-center justify-center">
                        <div className="w-2 h-2 rounded-full bg-white" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Stats Cards - 3 columns like real app */}
                <div className="px-3 pt-2 pb-1.5">
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-white/5 rounded-xl p-2 text-center border border-white/5">
                      <Navigation className="w-3.5 h-3.5 mx-auto mb-0.5 text-[#1d3557]" />
                      <div className="text-white text-[15px] font-bold leading-none">2.45</div>
                      <div className="text-white/40 text-[8px] mt-0.5">km</div>
                    </div>
                    <div className="bg-white/5 rounded-xl p-2 text-center border border-white/5">
                      <Clock className="w-3.5 h-3.5 mx-auto mb-0.5 text-[#3B82F6]" />
                      <div className="text-white text-[15px] font-bold leading-none">24:32</div>
                      <div className="text-white/40 text-[8px] mt-0.5">{t.gpsTracker.time}</div>
                    </div>
                    <div className="bg-white/5 rounded-xl p-2 text-center border border-white/5">
                      <Flame className="w-3.5 h-3.5 mx-auto mb-0.5 text-[#F59E0B]" />
                      <div className="text-white text-[15px] font-bold leading-none">147</div>
                      <div className="text-white/40 text-[8px] mt-0.5">kcal</div>
                    </div>
                  </div>
                </div>

                {/* Active buddies — workout partners live */}
                <div className="px-3 py-1.5">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-1 text-white/70 text-[9px]">
                      <Users className="w-3 h-3" />
                      <span>{t.gpsTracker.buddiesLabel || 'In allenamento ora'} · {(t.gpsTracker.buddies || []).length}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#16A34A] animate-pulse" />
                      <span className="text-[#16A34A] text-[8px] font-bold">{t.gpsTracker.live}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5">
                    {(t.gpsTracker.buddies || []).map((b, i) => {
                      const palette = [
                        { bg: 'bg-blue-500/20', border: 'border-blue-400/40', text: 'text-blue-300' },
                        { bg: 'bg-pink-500/20', border: 'border-pink-400/40', text: 'text-pink-300' },
                        { bg: 'bg-emerald-500/20', border: 'border-emerald-400/40', text: 'text-emerald-300' }
                      ][i % 3];
                      return (
                        <div key={i} className={`relative ${palette.bg} ${palette.border} border rounded-xl p-1.5 text-center`}>
                          {/* Live dot */}
                          <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-[#16A34A] animate-pulse" />
                          <div className={`w-7 h-7 mx-auto rounded-full ${palette.bg} border ${palette.border} ${palette.text} flex items-center justify-center text-[11px] font-bold mb-0.5`}>
                            {b.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="text-white text-[9px] font-semibold truncate">{b.name}</div>
                          <div className={`text-[8px] font-bold ${palette.text}`}>{b.km}km · {b.min}'</div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Action Buttons - Pause / Stop / Photo / Mic */}
                <div className="px-3 pb-3 pt-1.5 space-y-1.5">
                  <div className="flex gap-2">
                    <button className="flex-1 flex items-center justify-center gap-1 py-2 rounded-2xl bg-white/10 text-white font-semibold text-[10px]">
                      <Pause className="w-3 h-3" />
                      {t.gpsTracker.pause}
                    </button>
                    <button className="flex-1 flex items-center justify-center gap-1 py-2 rounded-2xl bg-[#E8334A] text-white font-semibold text-[10px]">
                      <Square className="w-3 h-3" />
                      {t.gpsTracker.stop}
                    </button>
                  </div>
                  {/* Photo + Mic — active during workout */}
                  <div className="flex gap-2">
                    <button className="flex-1 flex items-center justify-center gap-1 py-2 rounded-2xl bg-gradient-to-r from-[#16A34A]/30 to-[#22D3EE]/30 border border-[#16A34A]/40 text-white font-semibold text-[10px]">
                      <Camera className="w-3 h-3 text-[#16A34A]" />
                      {t.gpsTracker.cameraLabel || 'Foto'}
                    </button>
                    <button className="flex-1 flex items-center justify-center gap-1 py-2 rounded-2xl bg-gradient-to-r from-[#8B5CF6]/30 to-[#E8334A]/30 border border-[#8B5CF6]/40 text-white font-semibold text-[10px] relative">
                      <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                      <Mic className="w-3 h-3 text-[#8B5CF6]" />
                      {t.gpsTracker.micLabel || 'Voce'}
                    </button>
                  </div>
                </div>

                {/* Bottom Nav */}
                <div className="absolute bottom-0 left-0 right-0 bg-[#080D1A] border-t border-white/10 px-2 py-1.5 grid grid-cols-5 items-center">
                  {[
                    { icon: Home, label: t.nav?.home || 'Home', active: false },
                    { icon: MessageCircle, label: t.nav?.feed || 'Feed', active: false },
                    { isFab: true },
                    { icon: Trophy, label: t.nav?.challenges || 'Sfide', active: false },
                    { icon: User, label: t.nav?.profile || 'Profilo', active: false }
                  ].map((item, i) => (
                    <div key={`nav-walk-${i}`} className="flex flex-col items-center justify-center gap-0.5">
                      {item.isFab ? (
                        <div className="w-8 h-8 rounded-full bg-[#E8334A] flex items-center justify-center -mt-3 shadow-lg">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                            <line x1="12" y1="7" x2="12" y2="13" />
                            <line x1="9" y1="10" x2="15" y2="10" />
                          </svg>
                        </div>
                      ) : (
                        <>
                          <item.icon className={`w-3.5 h-3.5 ${item.active ? 'text-white' : 'text-white/40'}`} />
                          <span className={`text-[5.5px] font-medium uppercase text-center ${item.active ? 'text-white' : 'text-white/40'}`}>{item.label}</span>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
            
            {/* CTA below phone */}
            <div className="text-center mt-8">
            </div>
        </div>
      </section>

      {/* Exercise (Move) Section — guided micro-workouts + music */}
      <LandingExerciseSection lang={lang} />

      {/* Walkie-talkie Section */}
      <section className="py-20 px-4 relative overflow-hidden" data-testid="landing-walkie">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[#8B5CF6]/15 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#E8334A]/10 rounded-full blur-[100px]" />
        </div>
        <div className="max-w-6xl mx-auto relative z-10 grid lg:grid-cols-2 gap-12 items-center">
          {/* Copy */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#8B5CF6]/15 border border-[#8B5CF6]/30 mb-4">
              <Users className="w-4 h-4 text-[#8B5CF6]" />
              <span className="text-xs font-semibold text-[#8B5CF6]">{t.walkieTalkie.premium}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-white">
              {t.walkieTalkie.title}
              <span className="block bg-gradient-to-r from-[#8B5CF6] to-[#E8334A] bg-clip-text text-transparent">
                {t.walkieTalkie.subtitle}
              </span>
            </h2>
            <p className="text-gray-300 text-lg mb-8">{t.walkieTalkie.description}</p>
            <ul className="space-y-3">
              {t.walkieTalkie.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#16A34A] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300">{b}</span>
                </li>
              ))}
            </ul>
          </div>
          {/* Mockup */}
          <div className="flex justify-center">
            <div className="relative w-72 h-72 sm:w-80 sm:h-80 rounded-full bg-gradient-to-br from-[#8B5CF6]/30 to-[#E8334A]/30 border border-white/10 flex items-center justify-center">
              {/* Pulse rings */}
              <div className="absolute inset-0 rounded-full border-2 border-[#8B5CF6]/40 animate-ping" />
              <div className="absolute inset-4 rounded-full border-2 border-[#8B5CF6]/30 animate-ping" style={{ animationDelay: '0.5s' }} />
              {/* Center button */}
              <div className="relative w-40 h-40 rounded-full bg-gradient-to-br from-[#E8334A] to-[#8B5CF6] flex flex-col items-center justify-center shadow-2xl shadow-[#8B5CF6]/40">
                <Mic className="w-14 h-14 text-white mb-1" />
                <span className="text-[11px] text-white/90 font-semibold">{t.walkieTalkie.hint}</span>
              </div>
              {/* Live badge */}
              <div className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500 text-white text-xs font-bold shadow-lg">
                <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                {t.walkieTalkie.live}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Share Workout Section */}
      <section className="py-20 px-4 relative overflow-hidden" data-testid="landing-share-workout">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-[#16A34A]/10 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-[#22D3EE]/10 rounded-full blur-[100px]" />
        </div>
        <div className="max-w-6xl mx-auto relative z-10 grid lg:grid-cols-2 gap-12 items-center">
          {/* Mockup — clean phone-tracker frame, auto-sliding selfies only */}
          <div className="order-2 lg:order-1 flex justify-center">
            <div className="relative max-w-sm w-full bg-[#0a1628] rounded-[2.5rem] border border-white/10 overflow-hidden shadow-2xl">
              {/* Status bar — matches the tracker reference image */}
              <div className="flex items-center justify-between px-6 pt-3 pb-1 text-white text-[11px] font-semibold">
                <span>9:41</span>
                <span className="w-16 h-3 rounded-full bg-black" />
                <span className="inline-block w-5 h-2.5 rounded-sm border border-white/80 relative">
                  <span className="absolute inset-0.5 bg-white rounded-[1px]" />
                </span>
              </div>

              {/* Title bar — minimal "Camminata" header from the tracker */}
              <div className="flex items-center gap-3 px-4 py-2">
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                  <ChevronLeft className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-white text-lg font-bold flex-1">{t.shareWorkout.tracker?.title || 'Camminata'}</h3>
              </div>

              {/* Auto-sliding photo gallery — the ONLY content. Stacked
                  absolute imgs with opacity crossfade so each photo
                  fades smoothly into the next every 3.5s. */}
              <div className="relative aspect-[4/5] mx-3 mb-3 rounded-2xl overflow-hidden bg-black">
                {SHARE_HERO_PHOTOS.map((src, i) => (
                  <img
                    key={src}
                    src={src}
                    alt="Live workout selfie"
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === shareHeroIdx ? 'opacity-100' : 'opacity-0'}`}
                  />
                ))}
                {/* LIVE badge — single subtle accent */}
                <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                  LIVE
                </div>
                {/* Pagination dots — sync with active slide */}
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {SHARE_HERO_PHOTOS.map((_, i) => (
                    <span
                      key={i}
                      className={`h-1.5 rounded-full transition-all ${i === shareHeroIdx ? 'w-5 bg-white' : 'w-1.5 bg-white/40'}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
          {/* Copy */}
          <div className="order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#16A34A]/10 border border-[#16A34A]/30 mb-4">
              <Share2 className="w-4 h-4 text-[#16A34A]" />
              <span className="text-xs font-semibold text-[#16A34A]">{t.shareWorkout.tag}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-white">
              {t.shareWorkout.title}
              <span className="block bg-gradient-to-r from-[#16A34A] to-[#22D3EE] bg-clip-text text-transparent">
                {t.shareWorkout.subtitle}
              </span>
            </h2>
            <p className="text-gray-300 text-lg mb-8">{t.shareWorkout.description}</p>
            <ul className="space-y-3">
              {t.shareWorkout.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#16A34A] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* National Leaderboard Section */}
      <section className="py-20 px-4 relative overflow-hidden" data-testid="landing-leaderboard">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#F59E0B]/10 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#E8334A]/10 rounded-full blur-[100px]" />
        </div>
        <div className="max-w-6xl mx-auto relative z-10 grid lg:grid-cols-2 gap-12 items-center">
          {/* Mockup */}
          <div className="order-2 lg:order-1">
            <div className="relative max-w-sm mx-auto bg-[#0a1628] rounded-[2.5rem] border border-white/10 p-5 shadow-2xl">
              <div className="flex items-center gap-2 mb-4">
                <Trophy className="w-5 h-5 text-[#F59E0B]" />
                <span className="text-white font-bold">{t.leaderboard.title}</span>
                <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-[#F59E0B]/20 text-[#F59E0B] font-bold">🇮🇹 IT</span>
              </div>
              <div className="space-y-2">
                {[
                  { rank: 1, name: 'Marco', value: '47.2 km', icon: '🥇', accent: 'border-[#F59E0B] bg-[#F59E0B]/10' },
                  { rank: 2, name: 'Giulia', value: '42.8 km', icon: '🥈', accent: 'border-gray-400/40 bg-gray-400/5' },
                  { rank: 3, name: 'Luca', value: '38.5 km', icon: '🥉', accent: 'border-orange-400/40 bg-orange-400/5' },
                  { rank: 4, name: 'Sofia', value: '35.1 km', icon: '4', accent: 'border-white/10 bg-white/5' },
                  { rank: 12, name: 'Tu', value: '24.6 km', icon: '👤', accent: 'border-[#E8334A]/40 bg-[#E8334A]/10', highlight: true },
                ].map((row) => (
                  <div key={row.rank} className={`flex items-center gap-3 p-2.5 rounded-xl border ${row.accent}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${row.highlight ? 'bg-[#E8334A] text-white' : 'bg-white/10 text-white'}`}>
                      {row.icon}
                    </div>
                    <span className={`flex-1 text-sm ${row.highlight ? 'text-white font-bold' : 'text-white/90'}`}>{row.name}</span>
                    <span className={`text-sm font-mono ${row.highlight ? 'text-[#F59E0B] font-bold' : 'text-white/70'}`}>{row.value}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 p-2 rounded-xl bg-[#E8334A]/10 border border-[#E8334A]/30 text-[11px] text-[#E8334A] text-center">
                🔔 {t.leaderboard.bullets[2]}
              </div>
            </div>
          </div>
          {/* Copy */}
          <div className="order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/30 mb-4">
              <Medal className="w-4 h-4 text-[#F59E0B]" />
              <span className="text-xs font-semibold text-[#F59E0B]">{t.leaderboard.tag}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-white">
              {t.leaderboard.title}
              <span className="block bg-gradient-to-r from-[#F59E0B] to-[#E8334A] bg-clip-text text-transparent">
                {t.leaderboard.subtitle}
              </span>
            </h2>
            <p className="text-gray-300 text-lg mb-8">{t.leaderboard.description}</p>
            <ul className="space-y-3">
              {t.leaderboard.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#16A34A] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Languages Section */}
      <section className="py-20 px-4 relative overflow-hidden" data-testid="landing-languages">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 left-1/4 w-[400px] h-[400px] bg-[#22D3EE]/10 rounded-full blur-[120px]" />
        </div>
        <div className="max-w-5xl mx-auto relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#22D3EE]/10 border border-[#22D3EE]/30 mb-4">
            <Languages className="w-4 h-4 text-[#22D3EE]" />
            <span className="text-xs font-semibold text-[#22D3EE]">{t.languages.ctaTag}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-white">
            {t.languages.title}
            <span className="block bg-gradient-to-r from-[#22D3EE] to-[#16A34A] bg-clip-text text-transparent">
              {t.languages.subtitle}
            </span>
          </h2>
          <p className="text-gray-300 text-lg mb-12 max-w-2xl mx-auto">{t.languages.description}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            {t.languages.list.map((l) => (
              <div
                key={l.code}
                className="p-5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-[#22D3EE]/40 transition-all flex flex-col items-center gap-2"
              >
                <span className="text-4xl">{l.flag}</span>
                <span className="text-white font-semibold text-sm">{l.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Premium Pricing Section */}
      <section className="py-20 px-4 relative overflow-hidden" data-testid="landing-pricing">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#E8334A]/10 rounded-full blur-[140px]" />
        </div>
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-white">
              {t.pricing.title}
              <span className="block bg-gradient-to-r from-[#E8334A] to-[#F59E0B] bg-clip-text text-transparent">
                {t.pricing.subtitle}
              </span>
            </h2>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto mb-4">{t.pricing.description}</p>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#F59E0B] to-[#E8334A] text-white text-sm font-bold shadow-lg shadow-[#E8334A]/30">
              {t.pricing.lifetimeBadge}
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {/* Free */}
            <div className="p-6 sm:p-8 rounded-3xl bg-white/5 border border-white/10 flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold text-white">{t.pricing.free.name}</h3>
              </div>
              <div className="mb-6">
                <span className="text-5xl font-bold text-white">{t.pricing.free.price}</span>
                <span className="text-gray-400 ml-2">{t.pricing.free.period}</span>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {t.pricing.free.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#16A34A] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300 text-sm">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* Premium */}
            <div className="relative p-6 sm:p-8 rounded-3xl bg-gradient-to-br from-[#E8334A]/20 to-[#F59E0B]/20 border-2 border-[#E8334A]/50 flex flex-col shadow-2xl shadow-[#E8334A]/20">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-to-r from-[#E8334A] to-[#F59E0B] text-white text-xs font-bold uppercase tracking-wider shadow-lg">
                ⭐ {t.pricing.premium.badge}
              </div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Crown className="w-6 h-6 text-[#F59E0B]" />
                  {t.pricing.premium.name}
                </h3>
              </div>
              <div className="mb-6">
                <span className="text-5xl font-bold bg-gradient-to-r from-[#E8334A] to-[#F59E0B] bg-clip-text text-transparent">
                  {t.pricing.premium.price}
                </span>
                <span className="text-gray-400 ml-2">{t.pricing.premium.period}</span>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {t.pricing.premium.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-200 text-sm">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="p-8 sm:p-12 rounded-3xl bg-gradient-to-br from-[#16A34A]/20 to-[#22D3EE]/20 border border-[#16A34A]/30 relative overflow-hidden">
            {/* Free Badge */}
            <div className="inline-flex items-center gap-2 mb-6 px-6 py-2 rounded-full bg-gradient-to-r from-[#16A34A] to-[#22D3EE] text-white font-bold text-xl shadow-lg shadow-[#16A34A]/30">
              <span className="text-2xl">✓</span>
              {t.cta.subtitle}
            </div>
            
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">{t.cta.title}</h2>
            <p className="text-gray-300 mb-8 text-lg">{t.cta.description}</p>
            <p className="text-gray-400 text-sm mt-4">{t.cta.noCard}</p>
          </div>
        </div>
      </section>

      {/* Support / Info Request Card */}
      <section className="py-10 px-4" data-testid="landing-support-card-section">
        <div className="max-w-xl mx-auto">
          <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-[#0F1424] to-[#0A0F1F] p-8 text-center shadow-xl">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#E8334A]/15 text-[#E8334A] mb-4">
              <LifeBuoy className="w-7 h-7" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">{t.supportCard.title}</h3>
            <p className="text-gray-400 text-sm mb-6 leading-relaxed">
              {t.supportCard.description}
            </p>
            <Link
              to="/support-request"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#E8334A] hover:bg-[#d12d40] text-white font-semibold transition-colors"
              data-testid="landing-support-card-cta"
            >
              {t.supportCard.cta}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* App Store Badges Section */}
      <section className="py-12 px-4">
        <div className="max-w-xl mx-auto text-center">
          <p className="text-gray-400 text-sm mb-4">{t.appStores.downloadOn}</p>
          <div className="flex justify-center gap-4 flex-wrap">
            {/* App Store Badge — LIVE */}
            <a
              href="https://apps.apple.com/it/app/slimmy-app/id6771402343"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="landing-appstore-link"
              className="flex items-center gap-3 px-5 py-3 rounded-xl bg-white text-black hover:bg-gray-100 active:scale-95 transition-all shadow-lg shadow-white/10"
            >
              <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"/>
              </svg>
              <div className="text-left">
                <div className="text-[10px] text-gray-600 uppercase tracking-wide">{t.appStores.downloadOn}</div>
                <div className="text-sm font-semibold">{t.appStores.appStore}</div>
              </div>
            </a>

            {/* Google Play Badge — LIVE */}
            <a
              href="https://play.google.com/store/apps/details?id=com.dTkJNKhOvPWz.Slimmy"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="landing-playstore-link"
              className="flex items-center gap-3 px-5 py-3 rounded-xl bg-white text-black hover:bg-gray-100 active:scale-95 transition-all shadow-lg shadow-white/10"
            >
              <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3.609 1.814L13.792 12L3.609 22.186C3.227 21.801 3 21.27 3 20.69V3.31C3 2.73 3.227 2.199 3.609 1.814ZM14.851 13.058L17.205 15.412L5.618 21.893L14.851 13.058ZM18.275 10.698L20.625 12L18.275 13.302L15.681 12L18.275 10.698ZM5.618 2.107L17.205 8.588L14.851 10.942L5.618 2.107Z"/>
              </svg>
              <div className="text-left">
                <div className="text-[10px] text-gray-600 uppercase tracking-wide">{t.appStores.downloadOn}</div>
                <div className="text-sm font-semibold">{t.appStores.playStore}</div>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Footer - margin-top auto pushes to bottom */}
      <footer className="mt-auto py-8 px-4 border-t border-white/10 bg-[#080D1A]" style={{ paddingBottom: '50px' }} data-testid="landing-footer">
        <div className="max-w-6xl mx-auto flex flex-col gap-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <img src={LOGO} alt="Slimmy" className="h-6" />
              <span className="text-gray-500 text-sm">© 2026 {t.footer.rights}</span>
            </div>
            <div className="flex gap-6 text-sm text-gray-400">
              <Link
                to="/support-request"
                className="hover:text-white transition-colors"
                data-testid="landing-footer-support-link"
              >
                {t.footer.support}
              </Link>
              <Link to="/privacy" className="hover:text-white transition-colors">{t.footer.privacy}</Link>
              <Link to="/terms" className="hover:text-white transition-colors">{t.footer.terms}</Link>
            </div>
          </div>
          <div
            className="pt-4 border-t border-white/5 text-center text-xs text-gray-500 leading-relaxed"
            data-testid="landing-footer-company-info"
          >
            <div className="font-semibold text-gray-400">NOVA SERVICE FCO S.R.L.</div>
            <div>VIA DELLA FOCE MICINA 10/L - 00054 - FIUMICINO (ROMA) - ITALY</div>
            <div>Partita IVA: 17857241008 - Codice Fiscale: 17857241008</div>
          </div>
        </div>
      </footer>
      </div>
      <CookieConsent lang={lang} />
    </div>
  );
}
